
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { parseRecipeWithGemini, calculateNutritionFacts, RecipeParseError, getRecipeParseErrorMessage, type InstagramOwner } from '../services/geminiService';
import type { Recipe, Ingredient, IngredientAlternative, RecipeCategory, User, Language, InstructionStep, MealType, DietTypes, LifestyleTags, NutritionFactsPer100g, Chef, Household } from '../types';
import {
    EMPTY_DIET_TYPES,
    DIET_TYPE_CONFIG,
    normalizeDietTypes,
    resolveDietTypesForSave,
} from '../utils/dietTypes';
import {
    EMPTY_LIFESTYLE_TAGS,
    estimateLifestyleTags,
    LIFESTYLE_TAG_CONFIG,
    normalizeLifestyleTags,
    QUICK_PREP_MAX_MINUTES,
} from '../utils/lifestyleTags';
import SparklesIcon from './icons/SparklesIcon';
import { unifyUnit } from '../utils/unitConversion';
import { RECIPE_CATEGORIES } from '../utils/recipeCategories';
import RecipeAiProgressOverlay from './RecipeAiProgressOverlay';
import { useAiWorkflowProgress } from '../hooks/useAiWorkflowProgress';
import { AI_PARSE_LAST_STEP_INDEX } from '../utils/aiWorkflowSteps';
import UsersIcon from './icons/UsersIcon';
import DragHandleIcon from './icons/DragHandleIcon';
import TrashIcon from './icons/TrashIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import MeatIcon from './icons/MeatIcon';
import IngredientOptionsIcon from './icons/IngredientOptionsIcon';
import PlusIcon from './icons/PlusIcon';
import ClockIcon from './icons/ClockIcon';
import LayoutGridIcon from './icons/LayoutGridIcon';
import XIcon from './icons/XIcon';
import MergeIcon from './icons/MergeIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import GlobeIcon from './icons/GlobeIcon';
import ConfirmationModal from './ConfirmationModal';
import InfoNoticeModal from './InfoNoticeModal';
import SmartUnitCapsule from './SmartUnitCapsule';
import { getTranslation, isRTL } from '../utils/translations';
import NutritionFactsPanel from './NutritionFactsPanel';
import ChefAutocomplete from './ChefAutocomplete';
import { findChefByUrl, findChefByUrls, findChefByInstagramUsername, canUserPickChef, getChefWebsites } from '../utils/chefDisplay';
import { sanitizeExternalHref, sanitizeExternalHrefs } from '../utils/safeUrl';
import { creditChefFromParsedRecipe } from '../utils/resolveRecipeChefCredit';
import CopyButton from './CopyButton';
import { screenRecipeTextLocally } from '../utils/recipeContentGuard';
import { reportAndShowContentRejection, type ContentRejectionModalState } from '../utils/recipeContentRejection';
import RecipeContentRejectionModal from './RecipeContentRejectionModal';
import GeminiApiKeyModal from './GeminiApiKeyModal';
import RecipeTextInputModal from './RecipeTextInputModal';
import { formatRecipeText } from '../utils/formatRecipeText';
import {
    findConflictingDiscoverableRecipeForSharing,
    findConflictingDiscoverableRecipeForUrl,
} from '../utils/recipeExternalVideoDuplicate';
import RecipeImagePicker from './RecipeImagePicker';
import { buildAutoTaggedRecipeMetadata } from '../utils/recipeFilterEstimation';
import { getRecipeGalleryImageUrls, getRecipeImageFieldsGalleryUrls, appendRecipeGalleryImage, appendRecipeGalleryImageFromUrl, removeRecipeGalleryImageAt, uploadRecipeHeaderImage, uploadRecipeHeaderImageFromUrl, type RecipeImageFields } from '../utils/recipeMedia';
import {
    instructionContentForNameOnlyCheck,
    managedHouseholdIds,
    recipeActsAsTemporary,
    recipeContentIsComplete,
} from '../utils/temporaryRecipe';

interface AddRecipeModalProps {
    onClose: () => void;
    onAddRecipe: (recipe: Omit<Recipe, 'id'>) => Promise<string | void> | void;
    onUpdateRecipe: (recipe: Recipe) => Promise<void> | void;
    onRecipeSaved?: (recipe: Recipe) => void;
    /** Runs AI nutrition/translation after a pending recipe was already written. */
    onBackgroundFinalize?: (recipe: Recipe) => void;
    onDeleteRecipe?: (recipeId: string) => void;
    recipeToEdit?: Recipe;
    currentUser: User | null;
    households?: Household[];
    chefs?: Chef[];
    allRecipes?: Recipe[];
    canManage?: boolean;
    onResolveInstagramChef?: (owner: InstagramOwner) => Promise<Chef | null>;
    onViewExistingRecipe?: (recipe: Recipe) => void;
    onSaveUserProfile?: (updates: Partial<User>) => Promise<void>;
}

interface IngredientWithId extends Ingredient {
    _id: string;
    _sectionKey: string;
}

const newSectionKey = () => `sec-${Math.random().toString(36).slice(2, 11)}`;

const normalizeIngredientUnit = (unit: string | undefined): string => unifyUnit(unit || 'unit');

const normalizeIngredientUnits = (ingredient: Ingredient): Ingredient => ({
    ...ingredient,
    unit: normalizeIngredientUnit(ingredient.unit),
    alternatives: ingredient.alternatives?.map((alt) => ({
        ...alt,
        unit: normalizeIngredientUnit(alt.unit),
    })),
});

const withSectionKeys = (items: Ingredient[], defaultSectionLabel: string): IngredientWithId[] => {
    const keyByLabel = new Map<string, string>();
    return items.map((i) => {
        const sectionLabel = i.section ?? defaultSectionLabel;
        if (!keyByLabel.has(sectionLabel)) {
            keyByLabel.set(sectionLabel, newSectionKey());
        }
        return {
            ...normalizeIngredientUnits(i),
            section: sectionLabel,
            _sectionKey: keyByLabel.get(sectionLabel)!,
            _id: Math.random().toString(36).substr(2, 9),
        };
    });
};

const STANDARD_UNITS = ['kg', 'g', 'unit', 'ml', 'liter', 'tsp', 'tbsp', 'cup', 'container', 'tub', 'can', 'bottle', 'bag', 'box', 'roll', 'to taste', 'pinch', 'bunch', 'package', 'clove', 'head', 'stalk', 'shot', 'a_little', 'whole', 'half', 'quarter', 'third', 'to_choose'];

const stripIngredientForSave = ({ _id, _sectionKey, alternatives, ...rest }: IngredientWithId): Ingredient => {
    const cleanedAlternatives = alternatives
        ?.filter((alt) => alt.name.trim())
        .map(({ name, quantity, unit, isMeat }) => ({
            name: name.trim(),
            quantity,
            unit: normalizeIngredientUnit(unit),
            ...(isMeat ? { isMeat: true } : {}),
        }));
    return normalizeIngredientUnits({
        ...rest,
        ...(cleanedAlternatives && cleanedAlternatives.length > 0 ? { alternatives: cleanedAlternatives } : {}),
    });
};

interface RecipeEditorSnapshot {
    name: string;
    servings: number;
    prepTime: number;
    category: RecipeCategory;
    suitableFor: MealType[];
    dietTypes: DietTypes;
    lifestyleTags: LifestyleTags;
    isPublic: boolean;
    isSharedWithHouseholds: boolean;
    originLanguage: string;
    chefId: string | null;
    links: string[];
    originNote: string;
    ingredients: Ingredient[];
    instructions: Array<{ type: InstructionStep['type']; content: string; durationMinutes?: number }>;
    externalVideoId: string;
    externalVideoIdAliases: string[];
}

const normalizeInstructionSnapshot = (steps: InstructionStep[]) =>
    steps.map((step) => ({
        type: step.type,
        content: step.content.trim(),
        ...(step.type === 'step' && typeof step.durationMinutes === 'number' && step.durationMinutes > 0
            ? { durationMinutes: Math.round(step.durationMinutes) }
            : {}),
    }));

const parseInstructionDurationDraft = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
    return Math.round(parsed);
};

const normalizeIngredientSnapshot = (items: IngredientWithId[]): Ingredient[] =>
    items.map(stripIngredientForSave).map((ingredient) => ({
        ...ingredient,
        name: ingredient.name.trim(),
        section: ingredient.section?.trim() || undefined,
    }));

const instructionStepsFromRecipe = (recipe: Recipe, prepTabLabel: string): InstructionStep[] => {
    let steps: InstructionStep[] = [];
    if (typeof recipe.instructions === 'string') {
        steps = recipe.instructions.split('\n').filter((line) => line.trim()).map((line, idx) => ({
            id: `step-${idx}`,
            type: 'step' as const,
            content: line.replace(/^\d+[\.\)\-\s]+/, '').trim(),
        }));
    } else {
        steps = [...(recipe.instructions || [])];
    }

    if (steps.length > 0 && steps[0].type !== 'header') {
        steps.unshift({ id: 'h-auto', type: 'header', content: prepTabLabel });
    } else if (steps.length === 0) {
        steps = [{ id: 'h-init', type: 'header', content: prepTabLabel }];
    }

    return steps;
};

const buildSnapshotFromRecipe = (recipe: Recipe, prepTabLabel: string, ingredientsLabel: string): RecipeEditorSnapshot => ({
    name: recipe.name.trim(),
    servings: recipe.servings,
    prepTime: recipe.prepTime || 20,
    category: recipe.category || 'General',
    suitableFor: [...(recipe.suitableFor || [])].sort(),
    dietTypes: normalizeDietTypes(recipe.dietTypes, recipe.isKeto),
    lifestyleTags: {
        ...normalizeLifestyleTags(recipe.lifestyleTags),
        isQuickPrep: (recipe.prepTime || 20) <= QUICK_PREP_MAX_MINUTES,
    },
    isPublic: recipe.isPublic || false,
    isSharedWithHouseholds: (recipe.sharedWithHouseholds?.length ?? 0) > 0,
    originLanguage: recipe.originLanguage || 'en',
    chefId: recipe.chefId || null,
    links: sanitizeExternalHrefs(recipe.links || []),
    originNote: (recipe.originNote || '').trim(),
    ingredients: normalizeIngredientSnapshot(withSectionKeys(recipe.ingredients, ingredientsLabel)),
    instructions: normalizeInstructionSnapshot(instructionStepsFromRecipe(recipe, prepTabLabel)),
    externalVideoId: recipe.externalVideoId?.trim() || '',
    externalVideoIdAliases: [...(recipe.externalVideoIdAliases ?? [])].sort(),
});

const buildEmptyRecipeSnapshot = (prepTabLabel: string, ingredientsLabel: string): RecipeEditorSnapshot => ({
    name: '',
    servings: 2,
    prepTime: 20,
    category: 'General',
    suitableFor: [],
    dietTypes: { ...EMPTY_DIET_TYPES },
    lifestyleTags: { ...EMPTY_LIFESTYLE_TAGS, isQuickPrep: true },
    isPublic: false,
    isSharedWithHouseholds: false,
    originLanguage: 'en',
    chefId: null,
    links: [],
    originNote: '',
    ingredients: normalizeIngredientSnapshot(withSectionKeys([{
        name: '',
        quantity: 1,
        unit: 'unit',
        isMeat: false,
        includedInShoppingList: true,
        section: ingredientsLabel,
    }], ingredientsLabel)),
    instructions: normalizeInstructionSnapshot([{ id: 'h-init', type: 'header', content: prepTabLabel }]),
    externalVideoId: '',
    externalVideoIdAliases: [],
});

const snapshotsEqual = (left: RecipeEditorSnapshot, right: RecipeEditorSnapshot): boolean =>
    JSON.stringify(left) === JSON.stringify(right);

// Tracking/share params that don't change the destination (e.g. Instagram's ?igsh=).
const LINK_TRACKING_PARAMS = ['igsh', 'igshid', 'si', 'fbclid', 'gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/** Builds a comparison key so the same destination in different forms collapses to one link. */
const canonicalLinkKey = (link: string): string => {
    const trimmed = link.trim();
    try {
        const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
        const host = url.hostname.toLowerCase().replace(/^www\./, '');
        LINK_TRACKING_PARAMS.forEach((param) => url.searchParams.delete(param));
        const path = url.pathname.replace(/\/+$/, '');
        const search = url.searchParams.toString();
        return `${host}${path}${search ? `?${search}` : ''}`.toLowerCase();
    } catch {
        return trimmed.toLowerCase().replace(/\/+$/, '');
    }
};

/** Merges link lists preserving order, dropping empties and canonical duplicates. */
const mergeUniqueLinks = (...lists: string[][]): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const list of lists) {
        for (const link of list) {
            const value = sanitizeExternalHref(link);
            if (!value) continue;
            const key = canonicalLinkKey(value);
            if (seen.has(key)) continue;
            seen.add(key);
            result.push(value);
        }
    }
    return result;
};


const LinkIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
    </svg>
);

const ToggleChip: React.FC<{ label: string; checked: boolean; onClick: () => void; icon?: React.ReactNode; className?: string; disabled?: boolean; title?: string }> = ({ label, checked, onClick, icon, className, disabled = false, title }) => (
    <button 
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`px-2.5 sm:px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wide sm:tracking-widest transition-all border flex items-center justify-center gap-1.5 min-w-0 shrink whitespace-normal text-center
            ${checked 
                ? 'bg-brand-sage/10 text-brand-sage border-brand-sage shadow-sm' 
                : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${className || ''}
        `}
    >
        {icon}
        {label}
    </button>
);

const iconTouchTarget = 'w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shrink-0';

const CompactIngredientRow: React.FC<{
    ing: IngredientWithId;
    onChange: (id: string, field: keyof Ingredient, value: Ingredient[keyof Ingredient]) => void;
    onQuantityChange: (id: string, quantity: number, unit: string) => void;
    onDelete: (id: string) => void;
    isSelected: boolean;
    onToggleSelect: (id: string) => void;
    onSelect: (id: string) => void;
    language?: Language;
}> = ({ ing, onChange, onQuantityChange, onDelete, isSelected, onToggleSelect, onSelect, language }) => {
    const t = (k: Parameters<typeof getTranslation>[1]) => getTranslation(language, k);
    const [showAlternatives, setShowAlternatives] = useState((ing.alternatives ?? []).length > 0);
    const alternatives = ing.alternatives ?? [];

    const setAlternatives = (next: IngredientAlternative[]) => {
        onChange(ing._id, 'alternatives', next.length > 0 ? next : undefined);
        setShowAlternatives(next.length > 0);
    };

    const updateAlternative = (index: number, patch: Partial<IngredientAlternative>) => {
        const next = alternatives.map((alt, i) => (i === index ? { ...alt, ...patch } : alt));
        setAlternatives(next);
    };

    useEffect(() => {
        if ((ing.alternatives ?? []).length > 0) {
            setShowAlternatives(true);
        }
    }, [ing.alternatives]);

    const toggleAlternatives = () => {
        const nextVisible = !showAlternatives;
        setShowAlternatives(nextVisible);
        if (nextVisible && alternatives.length === 0) {
            setAlternatives([{ name: '', quantity: ing.quantity, unit: ing.unit, isMeat: ing.isMeat }]);
        }
    };

    const renderActionIcons = (className: string) => (
        <div className={className}>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    toggleAlternatives();
                }}
                className={`${iconTouchTarget} ${showAlternatives || alternatives.length > 0 ? 'text-brand-sage bg-brand-sage/10 hover:bg-brand-sage/15' : ''}`}
                title={t('ingredientOptions')}
            >
                <IngredientOptionsIcon className="w-5 h-5" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onChange(ing._id, 'isMeat', !ing.isMeat);
                }}
                className={`${iconTouchTarget} ${ing.isMeat ? 'text-rose-500 bg-rose-50 dark:bg-rose-900/30 hover:bg-rose-100 dark:hover:bg-rose-900/40' : ''}`}
                title={t('markAsMeat')}
            >
                <MeatIcon className={`w-5 h-5 ${ing.isMeat ? '' : 'grayscale opacity-60'}`} />
            </button>
            <button
                type="button"
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(ing._id);
                }}
                className={`${iconTouchTarget} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20`}
                title={t('delete')}
            >
                <TrashIcon className="w-4 h-4" />
            </button>
        </div>
    );

    return (
        <div className="last:border-b-0">
            <div
                className={`
                    flex flex-col border-b border-gray-100 dark:border-slate-800 group transition-all
                    ${ing.isMeat ? 'bg-rose-50/40 dark:bg-rose-900/10' : 'bg-white dark:bg-slate-900'}
                    ${isSelected ? 'ring-1 ring-brand-sage/30' : ''}
                `}
                onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('button, input, textarea, [data-no-select]')) return;
                    onToggleSelect(ing._id);
                }}
            >
                {/* Top line — always visible */}
                <div className="flex flex-row flex-nowrap items-center gap-2 w-full min-h-[48px] px-2">
                    <span
                        className="text-gray-300 dark:text-slate-600 cursor-grab active:cursor-grabbing shrink-0 select-none leading-none text-lg"
                        data-no-select
                        aria-hidden
                    >
                        ⋮
                    </span>
                    <input
                        type="text"
                        value={ing.name}
                        onChange={(e) => onChange(ing._id, 'name', e.target.value)}
                        onFocus={() => onSelect(ing._id)}
                        className="flex-1 min-w-0 truncate bg-transparent border-none focus:ring-0 p-0 font-bold text-slate-700 dark:text-slate-200 text-base placeholder:text-slate-300 dark:placeholder:text-slate-600 text-start"
                        placeholder={t('ingredientNamePlaceholder')}
                        data-no-select
                    />
                    {renderActionIcons(
                        'hidden md:flex opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity flex-row items-center gap-1 shrink-0',
                    )}
                    <div data-no-select className="shrink-0">
                        <SmartUnitCapsule
                            value={ing.quantity}
                            unit={ing.unit}
                            onChange={(val, unit) => onQuantityChange(ing._id, val, unit)}
                            language={language}
                            isMeat={ing.isMeat}
                            availableUnits={STANDARD_UNITS}
                            showFractionShortcuts
                            allowFractions
                            floatWhenOpen
                            zIndex={120}
                        />
                    </div>
                </div>

                {/* Mobile action drawer */}
                {isSelected && renderActionIcons(
                    'flex md:hidden flex-row items-center justify-start gap-4 bg-slate-50 dark:bg-slate-800/60 w-full p-2 rounded-b-md',
                )}
            </div>

            {showAlternatives && alternatives.length > 0 && (
                <div className="pb-3 px-2 ps-10 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">
                        {t('ingredientOptions')}
                    </p>
                    <div className="space-y-2 border-s-2 border-slate-100 dark:border-slate-800 ps-3">
                        {alternatives.map((alt, altIndex) => (
                            <div key={`${ing._id}-alt-${altIndex}`} className="flex flex-row flex-nowrap items-center gap-2 min-w-0">
                                <input
                                    type="text"
                                    value={alt.name}
                                    onChange={(e) => updateAlternative(altIndex, { name: e.target.value })}
                                    className="flex-1 min-w-0 truncate bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 text-start"
                                    placeholder={t('ingredientNamePlaceholder')}
                                />
                                <SmartUnitCapsule
                                    value={alt.quantity}
                                    unit={alt.unit}
                                    onChange={(val, unit) => {
                                        updateAlternative(altIndex, { quantity: val, unit });
                                    }}
                                    language={language}
                                    isMeat={alt.isMeat ?? false}
                                    availableUnits={STANDARD_UNITS}
                                    showFractionShortcuts
                                    allowFractions
                                    floatWhenOpen
                                    zIndex={120}
                                />
                                <button
                                    type="button"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        const next = alternatives.filter((_, i) => i !== altIndex);
                                        setAlternatives(next);
                                        if (next.length === 0) setShowAlternatives(false);
                                    }}
                                    className={`${iconTouchTarget} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20`}
                                >
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setAlternatives([
                            ...alternatives,
                            { name: '', quantity: ing.quantity, unit: ing.unit, isMeat: ing.isMeat },
                        ])}
                        className="mt-2 text-[10px] font-black uppercase tracking-widest text-brand-sage hover:underline flex items-center gap-1"
                    >
                        <PlusIcon className="w-3 h-3" /> {t('addIngredientOption')}
                    </button>
                </div>
            )}
        </div>
    );
};

const AddRecipeModal: React.FC<AddRecipeModalProps> = ({ onClose, onAddRecipe, onUpdateRecipe, onRecipeSaved, onBackgroundFinalize, onDeleteRecipe, recipeToEdit, currentUser, households = [], chefs = [], allRecipes = [], canManage = false, onResolveInstagramChef, onViewExistingRecipe, onSaveUserProfile }) => {
    const isEditMode = !!recipeToEdit;
    const lang = currentUser?.language || 'en';
    const rtl = isRTL(lang);
    const t = (key: any) => getTranslation(lang, key);
    const [sessionChefs, setSessionChefs] = useState<Chef[]>([]);
    const availableChefs = useMemo(() => {
        const byId = new Map(chefs.map((chef) => [chef.id, chef]));
        sessionChefs.forEach((chef) => byId.set(chef.id, chef));
        return Array.from(byId.values());
    }, [chefs, sessionChefs]);

    const [name, setName] = useState('');
    const [servings, setServings] = useState(2);
    const [prepTime, setPrepTime] = useState(20);
    const [ingredients, setIngredients] = useState<IngredientWithId[]>([]);
    const [instructionSteps, setInstructionSteps] = useState<InstructionStep[]>([]);
    const [links, setLinks] = useState<string[]>([]);
    const [tempLink, setTempLink] = useState('');
    const [originNote, setOriginNote] = useState('');
    const [chefId, setChefId] = useState<string | null>(null);
    const [autoCreditedChef, setAutoCreditedChef] = useState<Chef | null>(null);
    const [isPublic, setIsPublic] = useState(false);
    const [isSharedWithHouseholds, setIsSharedWithHouseholds] = useState(false);
    const [category, setCategory] = useState<RecipeCategory>('General');
    const [suitableFor, setSuitableFor] = useState<MealType[]>([]);
    const [dietTypes, setDietTypes] = useState<DietTypes>({ ...EMPTY_DIET_TYPES });
    const [lifestyleTags, setLifestyleTags] = useState<LifestyleTags>({ ...EMPTY_LIFESTYLE_TAGS });
    const canEditDietTypes = !isEditMode || currentUser?.uid === recipeToEdit?.ownerId;
    const [originLanguage, setOriginLanguage] = useState<Language | ''>(currentUser?.language || 'en');
    const [majorChangesMade, setMajorChangesMade] = useState(false);
    const [isBackgroundSaving, setIsBackgroundSaving] = useState(false);
    
    // AI State
    const [recipeUrl, setRecipeUrl] = useState('');
    const [recipeText, setRecipeText] = useState('');
    const hasAiAccess = !!(currentUser?.isAiEligible || currentUser?.encryptedGeminiKey);
    const [showAIBox, setShowAIBox] = useState(!isEditMode && hasAiAccess);
    const [showGeminiKeyModal, setShowGeminiKeyModal] = useState(false);
    const [showRecipeTextModal, setShowRecipeTextModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Saving State
    const [isSaving, setIsSaving] = useState(false);
    const aiProgress = useAiWorkflowProgress();
    const { advanceTo: advanceAiStep, reset: resetAiProgress, startAutoAdvance, stopAutoAdvance, stepKey: aiStepKey, stepIndex: aiStepIndex } = aiProgress;
    const [error, setError] = useState('');
    const [contentRejection, setContentRejection] = useState<ContentRejectionModalState | null>(null);
    const [showScraperNotice, setShowScraperNotice] = useState(false);
    const [externalVideoId, setExternalVideoId] = useState<string | undefined>(undefined);
    const [externalVideoIdAliases, setExternalVideoIdAliases] = useState<string[]>([]);
    const [existingRecipeConflict, setExistingRecipeConflict] = useState<Recipe | null>(null);
    const [sharingConflictNotice, setSharingConflictNotice] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showUnsavedChangesConfirm, setShowUnsavedChangesConfirm] = useState(false);
    const [baselineSnapshot, setBaselineSnapshot] = useState<RecipeEditorSnapshot | null>(null);
    const [editingInstructionStep, setEditingInstructionStep] = useState<{
        id: string;
        draft: string;
        draftDurationMinutes: string;
        stepNumber: number;
        isNew?: boolean;
    } | null>(null);
    
    const [collapsedIngSections, setCollapsedIngSections] = useState<Set<string>>(new Set());
    const [selectedIngredientId, setSelectedIngredientId] = useState<string | null>(null);
    const [collapsedPrepSections, setCollapsedPrepSections] = useState<Set<string>>(new Set());
    const [nutritionFacts, setNutritionFacts] = useState<NutritionFactsPer100g | undefined>();
    const [isNutritionCollapsed, setIsNutritionCollapsed] = useState(false);
    const [isRecalculatingNutrition, setIsRecalculatingNutrition] = useState(false);
    type PendingGalleryItem =
        | { kind: 'file'; file: File; previewUrl: string }
        | { kind: 'url'; sourceUrl: string; previewUrl: string };

    const [pendingGalleryItems, setPendingGalleryItems] = useState<PendingGalleryItem[]>([]);
    const [removeExistingImage, setRemoveExistingImage] = useState(false);
    const [storedImageFields, setStoredImageFields] = useState<RecipeImageFields | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [imageUploadError, setImageUploadError] = useState<string | null>(null);

    const canUseAi = hasAiAccess;
    const userGeminiKey = !currentUser?.isAiEligible ? currentUser?.encryptedGeminiKey : undefined;

    const managedHouseholdShareIds = useMemo(
        () => (currentUser ? managedHouseholdIds(currentUser, households) : []),
        [currentUser, households],
    );

    const inTemporaryLane = recipeActsAsTemporary(recipeToEdit);
    const isTemporaryDraft = inTemporaryLane
        && name.trim().length > 0
        && !recipeContentIsComplete(
            ingredients,
            instructionContentForNameOnlyCheck(instructionSteps, editingInstructionStep?.draft),
        );
    const storedRecipeStaysTemporary = !!recipeToEdit
        && recipeActsAsTemporary(recipeToEdit)
        && !recipeContentIsComplete(recipeToEdit.ingredients ?? [], recipeToEdit.instructions);

    const resolveSharedHouseholdIds = useCallback((temporary: boolean): string[] => {
        if (!isSharedWithHouseholds) return [];
        if (temporary) return managedHouseholdShareIds;
        return currentUser?.households ?? [];
    }, [isSharedWithHouseholds, managedHouseholdShareIds, currentUser?.households]);

    const visibilityFields = useCallback((temporary: boolean) => ({
        isPublic: temporary ? false : isPublic,
        isTemporary: temporary,
        sharedWithHouseholds: resolveSharedHouseholdIds(temporary),
    }), [isPublic, resolveSharedHouseholdIds]);

    useEffect(() => {
        if (!isTemporaryDraft) return;
        if (isPublic) setIsPublic(false);
        if (isSharedWithHouseholds && managedHouseholdShareIds.length === 0) {
            setIsSharedWithHouseholds(false);
        }
    }, [isTemporaryDraft, isPublic, isSharedWithHouseholds, managedHouseholdShareIds.length]);

    // Drag and Drop state
    const [draggedIngSection, setDraggedIngSection] = useState<string | null>(null);
    const [draggedPrepSection, setDraggedPrepSection] = useState<string | null>(null);
    const [dropTargetIngSection, setDropTargetIngSection] = useState<string | null>(null);
    const [dropTargetPrepSection, setDropTargetPrepSection] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [dragEnabledSection, setDragEnabledSection] = useState<string | null>(null);
    
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const nameRef = useRef<HTMLTextAreaElement>(null);
    const instructionStepEditorRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setIsMobile('ontouchstart' in window);
    }, []);

    useEffect(() => {
        if (!editingInstructionStep) return;
        const timer = window.setTimeout(() => instructionStepEditorRef.current?.focus(), 50);
        return () => window.clearTimeout(timer);
    }, [editingInstructionStep?.id]);

    useLayoutEffect(() => {
        if (nameRef.current) {
            nameRef.current.style.height = 'auto';
            nameRef.current.style.height = `${nameRef.current.scrollHeight}px`;
        }
    }, [name]);

    useEffect(() => {
        if (!isLoading && !isSaving) {
            resetAiProgress();
        }
    }, [isLoading, isSaving, resetAiProgress]);

    const persistedGalleryUrls = useMemo(() => {
        if (removeExistingImage) return [];
        if (storedImageFields) return getRecipeImageFieldsGalleryUrls(storedImageFields);
        if (recipeToEdit) return getRecipeGalleryImageUrls(recipeToEdit);
        return [];
    }, [removeExistingImage, storedImageFields, recipeToEdit]);

    const galleryPreviewUrls = useMemo(
        () => [...persistedGalleryUrls, ...pendingGalleryItems.map((item) => item.previewUrl)],
        [persistedGalleryUrls, pendingGalleryItems],
    );

    useEffect(() => {
        return () => {
            pendingGalleryItems.forEach((item) => {
                if (item.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(item.previewUrl);
                }
            });
        };
    }, [pendingGalleryItems]);

    useEffect(() => {
        if (isEditMode && recipeToEdit) {
            setName(recipeToEdit.name);
            setServings(recipeToEdit.servings);
            setPrepTime(recipeToEdit.prepTime || 20);
            
            const ings = withSectionKeys(recipeToEdit.ingredients, t('ingredients'));
            setIngredients(ings);

            const steps = instructionStepsFromRecipe(recipeToEdit, t('prepTab'));
            setInstructionSteps(steps);

            setCollapsedIngSections(new Set(ings.map(i => i._sectionKey)));

            const prepSections = new Set<string>();
            steps.forEach(s => { if (s.type === 'header' && s.id) prepSections.add(s.id); });
            setCollapsedPrepSections(prepSections);

            setLinks(recipeToEdit.links || []);
            setOriginNote(recipeToEdit.originNote || '');
            setChefId(recipeToEdit.chefId || null);
            setIsPublic(recipeToEdit.isPublic || false);
            setIsSharedWithHouseholds((recipeToEdit.sharedWithHouseholds && recipeToEdit.sharedWithHouseholds.length > 0) || false);
            setCategory(recipeToEdit.category || 'General');
            setSuitableFor(recipeToEdit.suitableFor || []);
            setDietTypes(normalizeDietTypes(recipeToEdit.dietTypes, recipeToEdit.isKeto));
            setLifestyleTags({
                ...normalizeLifestyleTags(recipeToEdit.lifestyleTags),
                isQuickPrep: (recipeToEdit.prepTime || 20) <= QUICK_PREP_MAX_MINUTES,
            });
            setOriginLanguage(recipeToEdit.originLanguage || currentUser?.language || 'en');
            setNutritionFacts(recipeToEdit.nutritionFactsPer100g);
            setExternalVideoId(recipeToEdit.externalVideoId);
            setExternalVideoIdAliases(recipeToEdit.externalVideoIdAliases ?? []);
            setPendingGalleryItems([]);
            setRemoveExistingImage(false);
            setStoredImageFields(
                recipeToEdit.imageUrl || recipeToEdit.imageUrls?.length
                    ? {
                        imageUrl: recipeToEdit.imageUrl,
                        imageUrls: recipeToEdit.imageUrls,
                        imageMediaIds: recipeToEdit.imageMediaIds,
                    }
                    : null,
            );
            setImageUploadError(null);
            setMajorChangesMade(false);
            setBaselineSnapshot(buildSnapshotFromRecipe(recipeToEdit, t('prepTab'), t('ingredients')));
        } else {
            const initialIngSec = t('ingredients');
            const initialIngSectionKey = newSectionKey();
            const initialPrepSec = t('prepTab');
            const initialPrepId = `ins-${Date.now()}-${Math.random()}`;
            setIngredients([{ 
                name: '', quantity: 1, unit: 'unit', isMeat: false, includedInShoppingList: true, section: initialIngSec, _sectionKey: initialIngSectionKey, _id: Math.random().toString(36).substr(2, 9) 
            }]);
            setInstructionSteps([{ id: initialPrepId, type: 'header', content: initialPrepSec }]);
            setCollapsedIngSections(new Set([initialIngSectionKey]));
            setCollapsedPrepSections(new Set([initialPrepId]));
            setLifestyleTags({ ...EMPTY_LIFESTYLE_TAGS, isQuickPrep: true });
            const defaultOriginLanguage = currentUser?.language || 'en';
            setOriginLanguage(defaultOriginLanguage);
            setPendingGalleryItems([]);
            setRemoveExistingImage(false);
            setStoredImageFields(null);
            setImageUploadError(null);
            setBaselineSnapshot({
                ...buildEmptyRecipeSnapshot(t('prepTab'), t('ingredients')),
                originLanguage: defaultOriginLanguage,
            });
        }
    }, [isEditMode, recipeToEdit, lang]);

    useEffect(() => {
        const isQuickPrep = prepTime <= QUICK_PREP_MAX_MINUTES;
        setLifestyleTags((prev) => (prev.isQuickPrep === isQuickPrep ? prev : { ...prev, isQuickPrep }));
    }, [prepTime]);

    const handleAddIngredient = (sectionKey?: string, atTop = false) => {
        setMajorChangesMade(true);
        const anchor = sectionKey
            ? ingredients.find(i => i._sectionKey === sectionKey)
            : ingredients[ingredients.length - 1];
        const targetKey = anchor?._sectionKey ?? newSectionKey();
        const targetSection = anchor?.section ?? t('ingredients');
        const newIng: IngredientWithId = { 
            name: '', quantity: 1, unit: 'unit', isMeat: false, includedInShoppingList: true, section: targetSection, _sectionKey: targetKey, _id: Math.random().toString(36).substr(2, 9) 
        };
        
        if (atTop) {
            const firstIndex = ingredients.findIndex(i => i._sectionKey === targetKey);
            if (firstIndex === -1) setIngredients([...ingredients, newIng]);
            else {
                const next = [...ingredients];
                next.splice(firstIndex, 0, newIng);
                setIngredients(next);
            }
        } else {
            const lastIndex = [...ingredients].reverse().findIndex(i => i._sectionKey === targetKey);
            if (lastIndex === -1) setIngredients([...ingredients, newIng]);
            else {
                const next = [...ingredients];
                const actualLastIdx = next.length - 1 - lastIndex;
                next.splice(actualLastIdx + 1, 0, newIng);
                setIngredients(next);
            }
        }
    };

    const handleAddIngredientSection = () => {
        setMajorChangesMade(true);
        const sectionKey = newSectionKey();
        const sectionName = `${t('section')} ${new Set(ingredients.map(i => i._sectionKey)).size + 1}`;
        const newIng: IngredientWithId = {
            name: '', quantity: 1, unit: 'unit', isMeat: false, includedInShoppingList: true,
            section: sectionName, _sectionKey: sectionKey, _id: Math.random().toString(36).substr(2, 9),
        };
        setIngredients([...ingredients, newIng]);
        setCollapsedIngSections(prev => new Set(prev).add(sectionKey));
    };

    const handleRenameSection = (sectionKey: string, newName: string) => {
        setMajorChangesMade(true);
        setIngredients(ingredients.map(i => i._sectionKey === sectionKey ? { ...i, section: newName } : i));
    };

    const toggleIngSection = (name: string) => {
        const next = new Set(collapsedIngSections);
        if (next.has(name)) next.delete(name); else next.add(name);
        setCollapsedIngSections(next);
    };

    const togglePrepSection = (id: string) => {
        const next = new Set(collapsedPrepSections);
        if (next.has(id)) next.delete(id); else next.add(id);
        setCollapsedPrepSections(next);
    };

    const handleHandleTouchStart = (id: string) => {
        if (!isMobile) return;
        longPressTimer.current = setTimeout(() => {
            setDragEnabledSection(id);
            if (window.navigator && window.navigator.vibrate) {
                window.navigator.vibrate(50);
            }
        }, 500);
    };

    const handleHandleTouchEnd = () => {
        if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };

    const clearIngSectionDragState = () => {
        setDraggedIngSection(null);
        setDropTargetIngSection(null);
        setDragEnabledSection(null);
    };

    const clearPrepSectionDragState = () => {
        setDraggedPrepSection(null);
        setDropTargetPrepSection(null);
        setDragEnabledSection(null);
    };

    const onDragStartIngSection = (e: React.DragEvent, sectionKey: string) => {
        if (isMobile && dragEnabledSection !== sectionKey) {
            e.preventDefault();
            return;
        }
        setDraggedIngSection(sectionKey);
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragOverIngSection = (e: React.DragEvent, sectionKey: string) => {
        e.preventDefault();
        if (draggedIngSection === sectionKey) return;
        setDropTargetIngSection(sectionKey);
    };

    const onDragEndIngSection = () => {
        clearIngSectionDragState();
    };

    const onDropIngSection = (e: React.DragEvent, targetSectionKey: string) => {
        e.preventDefault();
        if (!draggedIngSection || draggedIngSection === targetSectionKey) {
            clearIngSectionDragState();
            return;
        }

        const nextIngredients = [...ingredients];
        const itemsToMove = nextIngredients.filter(i => i._sectionKey === draggedIngSection);
        const filtered = nextIngredients.filter(i => i._sectionKey !== draggedIngSection);
        
        const targetIndex = filtered.findIndex(i => i._sectionKey === targetSectionKey);
        
        if (targetIndex !== -1) {
            filtered.splice(targetIndex, 0, ...itemsToMove);
        } else {
            filtered.push(...itemsToMove);
        }
        
        setIngredients(filtered);
        clearIngSectionDragState();
    };

    const onDragStartPrepSection = (e: React.DragEvent, sectionId: string) => {
        if (isMobile && dragEnabledSection !== sectionId) {
            e.preventDefault();
            return;
        }
        setDraggedPrepSection(sectionId);
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragOverPrepSection = (e: React.DragEvent, sectionId: string) => {
        e.preventDefault();
        if (draggedPrepSection === sectionId) return;
        setDropTargetPrepSection(sectionId);
    };

    const onDragEndPrepSection = () => {
        clearPrepSectionDragState();
    };

    const onDropPrepSection = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedPrepSection || draggedPrepSection === targetId) {
            clearPrepSectionDragState();
            return;
        }

        const nextSteps = [...instructionSteps];
        const startIndex = nextSteps.findIndex(s => s.id === draggedPrepSection);
        if (startIndex === -1) {
            clearPrepSectionDragState();
            return;
        }

        let endIndex = startIndex + 1;
        while (endIndex < nextSteps.length && nextSteps[endIndex].type !== 'header') {
            endIndex++;
        }

        const itemsToMove = nextSteps.splice(startIndex, endIndex - startIndex);
        const targetIndex = nextSteps.findIndex(s => s.id === targetId);

        if (targetIndex !== -1) {
            nextSteps.splice(targetIndex, 0, ...itemsToMove);
        } else {
            nextSteps.push(...itemsToMove);
        }

        setInstructionSteps(nextSteps);
        clearPrepSectionDragState();
    };

    const handleInstructionAddStep = (headerIndex: number) => {
        setMajorChangesMade(true);
        let lastIndex = headerIndex;
        for (let i = headerIndex + 1; i < instructionSteps.length; i++) {
            if (instructionSteps[i].type === 'header') break;
            lastIndex = i;
        }

        const next = [...instructionSteps];
        const newId = `ins-${Date.now()}-${Math.random()}`;
        const newStep: InstructionStep = { id: newId, type: 'step', content: '' };
        next.splice(lastIndex + 1, 0, newStep);
        setInstructionSteps(next);

        const stepNumber = next.slice(headerIndex + 1, lastIndex + 2).filter((s) => s.type === 'step').length;
        setEditingInstructionStep({ id: newId, draft: '', draftDurationMinutes: '', stepNumber, isNew: true });
    };

    const openInstructionStepEditor = (step: InstructionStep, stepNumber: number) => {
        if (!step.id) return;
        setEditingInstructionStep({
            id: step.id,
            draft: step.content,
            draftDurationMinutes: typeof step.durationMinutes === 'number' && step.durationMinutes > 0
                ? String(step.durationMinutes)
                : '',
            stepNumber,
        });
    };

    const applyInstructionStepEdit = (id: string, content: string, durationMinutes: number | undefined) => {
        setMajorChangesMade(true);
        setInstructionSteps(instructionSteps.map((step) => {
            if (step.id !== id) return step;
            const next: InstructionStep = { ...step, content };
            if (durationMinutes != null) {
                next.durationMinutes = durationMinutes;
            } else {
                delete next.durationMinutes;
            }
            return next;
        }));
    };

    const acceptInstructionStepEdit = () => {
        if (!editingInstructionStep) return;
        const trimmed = editingInstructionStep.draft.trim();
        const durationMinutes = parseInstructionDurationDraft(editingInstructionStep.draftDurationMinutes);
        if (!trimmed) {
            if (editingInstructionStep.isNew) {
                handleInstructionDelete(editingInstructionStep.id);
            }
        } else {
            applyInstructionStepEdit(editingInstructionStep.id, editingInstructionStep.draft, durationMinutes);
        }
        setEditingInstructionStep(null);
    };

    const discardInstructionStepEdit = () => {
        if (editingInstructionStep?.isNew) {
            handleInstructionDelete(editingInstructionStep.id);
        }
        setEditingInstructionStep(null);
    };

    const handleInstructionAddHeader = () => {
        setMajorChangesMade(true);
        const newId = `ins-h-${Date.now()}-${Math.random()}`;
        const newHeader: InstructionStep = { id: newId, type: 'header', content: t('newSection') };
        setInstructionSteps([...instructionSteps, newHeader]);
        setCollapsedPrepSections(prev => new Set(prev).add(newId));
    };

    const handleInstructionChange = (id: string, content: string) => {
        setMajorChangesMade(true);
        setInstructionSteps(instructionSteps.map(s => s.id === id ? { ...s, content } : s));
    };

    const handleMoveInstruction = (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= instructionSteps.length) return;

        const current = instructionSteps[index];
        const target = instructionSteps[targetIndex];
        if (current.type !== 'step' || target.type !== 'step') return;

        setMajorChangesMade(true);
        const newSteps = [...instructionSteps];
        newSteps[index] = target;
        newSteps[targetIndex] = current;
        setInstructionSteps(newSteps);
    };

    const handleInstructionDelete = (id: string) => {
        setMajorChangesMade(true);
        const index = instructionSteps.findIndex(s => s.id === id);
        if (index === -1) return;

        const item = instructionSteps[index];
        if (item.type === 'header') {
            let countToRemove = 1;
            for (let i = index + 1; i < instructionSteps.length; i++) {
                if (instructionSteps[i].type === 'header') break;
                countToRemove++;
            }
            
            const next = [...instructionSteps];
            next.splice(index, countToRemove);
            
            if (next.filter(s => s.type === 'header').length === 0) {
                 next.unshift({ id: `ins-${Date.now()}-${Math.random()}`, type: 'header', content: t('prepTab') });
            }
            
            setInstructionSteps(next);
        } else {
            setInstructionSteps(instructionSteps.filter(s => s.id !== id));
        }
    };

    const handleUniteWithPrevious = (headerId: string) => {
        setMajorChangesMade(true);
        setInstructionSteps(instructionSteps.filter(s => s.id !== headerId));
    };

    const toggleSuitableFor = (meal: MealType) => {
        setSuitableFor(prev => {
            if (prev.includes(meal)) return prev.filter(m => m !== meal);
            return [...prev, meal];
        });
    };

    const handleAddLink = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const url = tempLink.trim();
        if (!url) return;
        setLinks(mergeUniqueLinks(links, [url]));
        setTempLink('');
        const matchedChef = findChefByUrl(availableChefs, url, currentUser?.uid);
        if (matchedChef) setChefId(matchedChef.id);
    };

    const handleDeleteLink = (index: number) => {
        setLinks(links.filter((_, i) => i !== index));
    };

    const computeNutritionFacts = async (): Promise<NutritionFactsPer100g | undefined> => {
        if (!canUseAi || ingredients.length === 0) return undefined;
        return calculateNutritionFacts(
            ingredients.map(({ _id, _sectionKey, alternatives, ...rest }) => rest),
            servings,
            (originLanguage || 'en') as Language,
            userGeminiKey
        );
    };

    const handleRecalculateNutrition = async () => {
        if (!isEditMode || !recipeToEdit || !canManage) return;
        setIsRecalculatingNutrition(true);
        try {
            const facts = await computeNutritionFacts();
            if (!facts) return;

            setNutritionFacts(facts);
            const lang = (originLanguage || 'en') as Language;
            const englishTranslation = lang !== 'en'
                ? { ...recipeToEdit.englishTranslation, nutritionFactsPer100g: facts }
                : recipeToEdit.englishTranslation;

            await onUpdateRecipe({
                ...recipeToEdit,
                nutritionFactsPer100g: facts,
                englishTranslation,
                updatedAt: new Date().toISOString(),
            });
        } finally {
            setIsRecalculatingNutrition(false);
        }
    };

    const resolveAuthorizedChefId = useCallback(() => {
        if (!chefId) return null;
        const chef = availableChefs.find((c) => c.id === chefId);
        return chef && canUserPickChef(chef, currentUser?.uid) ? chefId : null;
    }, [chefId, availableChefs, currentUser?.uid]);

    const persistMetadata = useCallback(async () => {
        if (!isEditMode || !recipeToEdit) return;

        const recipeData: Recipe = {
            ...recipeToEdit,
            ...(storedImageFields ?? {}),
            name,
            servings,
            prepTime,
            category,
            suitableFor,
            dietTypes: resolveDietTypesForSave(
                currentUser?.uid,
                recipeToEdit.ownerId,
                recipeToEdit.dietTypes,
                dietTypes,
            ),
            lifestyleTags: { ...lifestyleTags },
            ...visibilityFields(storedRecipeStaysTemporary),
            originLanguage: originLanguage || 'en',
            chefId: resolveAuthorizedChefId(),
            updatedAt: new Date().toISOString(),
        };

        await onUpdateRecipe(recipeData);
    }, [
        isEditMode,
        recipeToEdit,
        name,
        servings,
        prepTime,
        category,
        suitableFor,
        dietTypes,
        lifestyleTags,
        isPublic,
        isSharedWithHouseholds,
        originLanguage,
        resolveAuthorizedChefId,
        onUpdateRecipe,
        currentUser?.uid,
        currentUser?.households,
        storedImageFields,
        visibilityFields,
        storedRecipeStaysTemporary,
    ]);

    const uploadRecipeImageNow = useCallback(async (
        recipe: Recipe,
        input: { file?: File; sourceUrl?: string; remove?: boolean; removeIndex?: number },
    ): Promise<RecipeImageFields | false> => {
        if (!recipe.id) return false;

        setIsUploadingImage(true);
        setImageUploadError(null);

        try {
            const existingBase: RecipeImageFields | undefined = storedImageFields
                ?? (recipe.imageUrl || recipe.imageUrls?.length
                    ? {
                        imageUrl: recipe.imageUrl,
                        imageUrls: recipe.imageUrls,
                        imageMediaIds: recipe.imageMediaIds,
                    }
                    : undefined);

            if (input.remove) {
                const cleared: RecipeImageFields = { imageUrls: [], imageMediaIds: [] };
                await onUpdateRecipe({
                    ...recipe,
                    ...cleared,
                    imageUrl: undefined,
                    updatedAt: new Date().toISOString(),
                });
                setStoredImageFields(cleared);
                setPendingGalleryItems([]);
                setRemoveExistingImage(false);
                return cleared;
            }

            if (input.removeIndex != null) {
                const base = existingBase ?? { imageUrls: [], imageMediaIds: [] };
                const imageFields = removeRecipeGalleryImageAt(base, input.removeIndex);
                await onUpdateRecipe({
                    ...recipe,
                    ...imageFields,
                    imageUrl: imageFields.imageUrls?.length ? imageFields.imageUrl : undefined,
                    updatedAt: new Date().toISOString(),
                });
                setStoredImageFields(imageFields.imageUrls?.length ? imageFields : null);
                setRemoveExistingImage(false);
                return imageFields;
            }

            const hasExistingGallery = getRecipeImageFieldsGalleryUrls(existingBase).length > 0;
            let imageFields: RecipeImageFields;
            if (input.file) {
                imageFields = hasExistingGallery
                    ? await appendRecipeGalleryImage(recipe.id, input.file, existingBase)
                    : await uploadRecipeHeaderImage(recipe.id, input.file, existingBase);
            } else if (input.sourceUrl) {
                imageFields = hasExistingGallery
                    ? await appendRecipeGalleryImageFromUrl(recipe.id, input.sourceUrl, existingBase)
                    : await uploadRecipeHeaderImageFromUrl(recipe.id, input.sourceUrl, existingBase);
            } else {
                return existingBase ?? false;
            }

            await onUpdateRecipe({
                ...recipe,
                ...imageFields,
                updatedAt: new Date().toISOString(),
            });
            setStoredImageFields(imageFields);
            setRemoveExistingImage(false);
            return imageFields;
        } catch (uploadError) {
            console.error('Recipe image upload failed:', uploadError);
            setImageUploadError(t('recipePhotoUploadFailed'));
            return false;
        } finally {
            setIsUploadingImage(false);
        }
    }, [storedImageFields, onUpdateRecipe, t]);

    const buildCurrentSnapshot = useCallback((): RecipeEditorSnapshot => {
        const effectiveInstructionSteps = editingInstructionStep
            ? instructionSteps.map((step) => {
                if (step.id !== editingInstructionStep.id) return step;
                const next: InstructionStep = { ...step, content: editingInstructionStep.draft };
                const durationMinutes = parseInstructionDurationDraft(editingInstructionStep.draftDurationMinutes);
                if (durationMinutes != null) {
                    next.durationMinutes = durationMinutes;
                } else {
                    delete next.durationMinutes;
                }
                return next;
            })
            : instructionSteps;

        return {
            name: name.trim(),
            servings,
            prepTime,
            category,
            suitableFor: [...suitableFor].sort(),
            dietTypes: resolveDietTypesForSave(
                currentUser?.uid,
                recipeToEdit ? recipeToEdit.ownerId : currentUser?.uid,
                recipeToEdit?.dietTypes,
                dietTypes,
            ),
            lifestyleTags: { ...lifestyleTags },
            isPublic,
            isSharedWithHouseholds,
            originLanguage: originLanguage || 'en',
            chefId: resolveAuthorizedChefId(),
            links: sanitizeExternalHrefs(links),
            originNote: originNote.trim(),
            ingredients: normalizeIngredientSnapshot(ingredients),
            instructions: normalizeInstructionSnapshot(effectiveInstructionSteps),
            externalVideoId: externalVideoId?.trim() || '',
            externalVideoIdAliases: [...externalVideoIdAliases].sort(),
        };
    }, [
        editingInstructionStep,
        instructionSteps,
        name,
        servings,
        prepTime,
        category,
        suitableFor,
        dietTypes,
        lifestyleTags,
        isPublic,
        isSharedWithHouseholds,
        originLanguage,
        resolveAuthorizedChefId,
        links,
        originNote,
        ingredients,
        externalVideoId,
        externalVideoIdAliases,
        currentUser?.uid,
        recipeToEdit,
    ]);

    const isBlankNewRecipe = useCallback((): boolean => {
        if (isEditMode) return false;
        return name.trim().length === 0
            && originNote.trim().length === 0
            && links.every((link) => link.trim().length === 0)
            && ingredients.every((ing) => ing.name.trim().length === 0)
            && instructionSteps.every((step) => step.type !== 'step' || step.content.trim().length === 0);
    }, [isEditMode, name, originNote, links, ingredients, instructionSteps]);

    const hasUnsavedChanges = useMemo(() => {
        if (!baselineSnapshot) return false;
        if (isBlankNewRecipe()) return false;
        return !snapshotsEqual(buildCurrentSnapshot(), baselineSnapshot);
    }, [baselineSnapshot, buildCurrentSnapshot, isBlankNewRecipe]);

    const resolveInstructionStepsForSave = useCallback((): InstructionStep[] => {
        if (!editingInstructionStep) return instructionSteps;

        const trimmed = editingInstructionStep.draft.trim();
        if (!trimmed) {
            if (editingInstructionStep.isNew) {
                return instructionSteps.filter((step) => step.id !== editingInstructionStep.id);
            }
            return instructionSteps;
        }

        return instructionSteps.map((step) => {
            if (step.id !== editingInstructionStep.id) return step;
            const next: InstructionStep = { ...step, content: editingInstructionStep.draft };
            const durationMinutes = parseInstructionDurationDraft(editingInstructionStep.draftDurationMinutes);
            if (durationMinutes != null) {
                next.durationMinutes = durationMinutes;
            } else {
                delete next.durationMinutes;
            }
            return next;
        });
    }, [editingInstructionStep, instructionSteps]);

    const saveRecipe = useCallback(async (options?: { closeAfterMetadataSave?: boolean }): Promise<boolean> => {
        const stepsForSave = resolveInstructionStepsForSave();
        if (editingInstructionStep) {
            setInstructionSteps(stepsForSave);
            setEditingInstructionStep(null);
        }

        const pendingSnapshot: RecipeEditorSnapshot = {
            ...buildCurrentSnapshot(),
            instructions: normalizeInstructionSnapshot(stepsForSave),
        };
        const isMetadataOnlySave = isEditMode && recipeToEdit && baselineSnapshot
            && JSON.stringify(pendingSnapshot.ingredients) === JSON.stringify(baselineSnapshot.ingredients)
            && JSON.stringify(pendingSnapshot.instructions) === JSON.stringify(baselineSnapshot.instructions);
        const hasPendingImageChanges = pendingGalleryItems.length > 0 || removeExistingImage;

        if (isMetadataOnlySave && !hasPendingImageChanges) {
            setIsBackgroundSaving(true);
            try {
                await persistMetadata();
                setBaselineSnapshot(pendingSnapshot);
                if (options?.closeAfterMetadataSave && recipeToEdit) {
                    onRecipeSaved?.({
                        ...recipeToEdit,
                        ...(storedImageFields ?? {}),
                        name,
                        servings,
                        prepTime,
                        category,
                        suitableFor,
                        dietTypes: resolveDietTypesForSave(
                            currentUser?.uid,
                            recipeToEdit.ownerId,
                            recipeToEdit.dietTypes,
                            dietTypes,
                        ),
                        lifestyleTags: { ...lifestyleTags },
                        ...visibilityFields(storedRecipeStaysTemporary),
                        originLanguage: originLanguage || 'en',
                        chefId: resolveAuthorizedChefId() ?? undefined,
                        updatedAt: new Date().toISOString(),
                    });
                }
                return true;
            } finally {
                setIsBackgroundSaving(false);
            }
        }

        const candidateText = [
            name,
            originNote,
            ingredients.map((i) => i.name).join(' '),
            stepsForSave.map((s) => s.content).join(' '),
        ].join('\n');

        const localScreen = screenRecipeTextLocally(candidateText);
        if (!localScreen.allowed) {
            await reportAndShowContentRejection(
                {
                    userId: currentUser?.uid,
                    code: localScreen.code ?? 'not_a_recipe',
                    source: 'recipe_save',
                    textSample: candidateText,
                },
                setContentRejection,
            );
            return false;
        }

        const hasIngredientText = ingredients.some((i) => i.name.trim().length > 0);
        const hasInstructionText = stepsForSave.some(
            (s) => (s.type === 'step' || s.type === 'header') && s.content.trim().length > 0,
        );
        const saveAsTemporary = inTemporaryLane
            && name.trim().length > 0
            && !recipeContentIsComplete(ingredients, stepsForSave);
        if ((!hasIngredientText || !hasInstructionText) && !saveAsTemporary) {
            setContentRejection({ isOpen: true, variant: 'not_recipe' });
            return false;
        }

        setIsSaving(true);
        advanceAiStep(6);
        try {
            const visibility = visibilityFields(saveAsTemporary);
            const sharingConflict = await findConflictingDiscoverableRecipeForSharing(
                externalVideoId,
                externalVideoIdAliases,
                visibility.isPublic,
                visibility.sharedWithHouseholds,
                recipeToEdit?.id,
            );
            if (sharingConflict) {
                setExistingRecipeConflict(sharingConflict);
                setSharingConflictNotice(true);
                return false;
            }

            const needsTranslation = !saveAsTemporary
                && (!isEditMode || !isMetadataOnlySave)
                && !!originLanguage
                && originLanguage !== 'en'
                && canUseAi;
            const needsNutrition = !saveAsTemporary && (!isEditMode || !isMetadataOnlySave) && canUseAi;
            const needsBackgroundFinalize = needsNutrition || needsTranslation;

            const now = new Date().toISOString();
            const authorizedChefId = resolveAuthorizedChefId();
            const resolvedDietTypes = resolveDietTypesForSave(
                currentUser?.uid,
                recipeToEdit ? recipeToEdit.ownerId : currentUser?.uid,
                recipeToEdit?.dietTypes,
                dietTypes,
            );
            const autoTagged = buildAutoTaggedRecipeMetadata({
                name,
                prepTime,
                category,
                ingredients,
                instructions: stepsForSave,
                suitableFor,
                dietTypes: resolvedDietTypes,
            });
            const recipeData = {
                name,
                ingredients: saveAsTemporary
                    ? ingredients.map(stripIngredientForSave).filter((ingredient) => ingredient.name.trim().length > 0)
                    : ingredients.map(stripIngredientForSave),
                instructions: saveAsTemporary
                    ? stepsForSave.filter((step) => step.type === 'step' && step.content.trim().length > 0)
                    : stepsForSave,
                servings,
                prepTime,
                links: sanitizeExternalHrefs(links),
                originNote,
                ...(authorizedChefId ? { chefId: authorizedChefId } : {}),
                ...visibility,
                category: autoTagged.category,
                suitableFor: autoTagged.suitableFor,
                dietTypes: autoTagged.dietTypes,
                lifestyleTags: { ...lifestyleTags },
                originLanguage: originLanguage || 'en',
                ...(recipeToEdit?.englishTranslation ? { englishTranslation: recipeToEdit.englishTranslation } : {}),
                ...(nutritionFacts ? { nutritionFactsPer100g: nutritionFacts } : {}),
                ...(externalVideoId ? { externalVideoId } : {}),
                ...(externalVideoIdAliases.length > 0 ? { externalVideoIdAliases } : {}),
                ...(storedImageFields
                    ? storedImageFields
                    : !removeExistingImage && pendingGalleryItems.length === 0 && recipeToEdit?.imageUrl
                        ? {
                            imageUrl: recipeToEdit.imageUrl,
                            imageUrls: recipeToEdit.imageUrls,
                            imageMediaIds: recipeToEdit.imageMediaIds,
                        }
                        : {}),
                ownerId: recipeToEdit ? recipeToEdit.ownerId : (currentUser?.uid || ''),
                householdId: recipeToEdit ? recipeToEdit.householdId : (currentUser?.activeHouseholdId || ''),
                updatedAt: now,
                saveStatus: needsBackgroundFinalize ? 'pending' as const : 'ready' as const,
            };

            let savedRecipe = {
                ...(recipeToEdit ?? {}),
                ...recipeData,
                id: recipeToEdit?.id ?? '',
                createdAt: recipeToEdit?.createdAt ?? now,
            } as Recipe;

            if (isEditMode && recipeToEdit) {
                await onUpdateRecipe(savedRecipe);
            } else {
                const newId = await onAddRecipe(recipeData);
                if (typeof newId !== 'string') return false;
                savedRecipe.id = newId;
                savedRecipe.createdAt = now;
            }

            if (pendingGalleryItems.length > 0 && savedRecipe.id) {
                for (const item of pendingGalleryItems) {
                    const imageFields = await uploadRecipeImageNow(
                        savedRecipe,
                        item.kind === 'file'
                            ? { file: item.file }
                            : { sourceUrl: item.sourceUrl },
                    );
                    if (imageFields === false) return false;
                    if (imageFields) {
                        savedRecipe = { ...savedRecipe, ...imageFields };
                    }
                }
                setPendingGalleryItems((items) => {
                    items.forEach((item) => {
                        if (item.previewUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(item.previewUrl);
                        }
                    });
                    return [];
                });
            } else if (removeExistingImage && savedRecipe.id) {
                const imageFields = await uploadRecipeImageNow(savedRecipe, { remove: true });
                if (imageFields === false) return false;
                savedRecipe = {
                    ...savedRecipe,
                    imageUrl: undefined,
                    imageUrls: [],
                    imageMediaIds: [],
                };
            } else if (storedImageFields) {
                savedRecipe = { ...savedRecipe, ...storedImageFields };
            }

            onRecipeSaved?.(savedRecipe);
            if (needsBackgroundFinalize) {
                onBackgroundFinalize?.(savedRecipe);
            }
            setMajorChangesMade(false);
            setBaselineSnapshot(pendingSnapshot);
            return true;
        } finally {
            setIsSaving(false);
        }
    }, [
        editingInstructionStep,
        resolveInstructionStepsForSave,
        isEditMode,
        recipeToEdit,
        baselineSnapshot,
        name,
        persistMetadata,
        buildCurrentSnapshot,
        onRecipeSaved,
        onBackgroundFinalize,
        currentUser,
        originNote,
        ingredients,
        instructionSteps,
        isSharedWithHouseholds,
        externalVideoId,
        externalVideoIdAliases,
        isPublic,
        inTemporaryLane,
        visibilityFields,
        storedRecipeStaysTemporary,
        originLanguage,
        servings,
        prepTime,
        category,
        suitableFor,
        dietTypes,
        lifestyleTags,
        links,
        nutritionFacts,
        resolveAuthorizedChefId,
        onUpdateRecipe,
        onAddRecipe,
        canUseAi,
        advanceAiStep,
        pendingGalleryItems,
        removeExistingImage,
        storedImageFields,
        uploadRecipeImageNow,
    ]);

    const handleCancelRequest = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowUnsavedChangesConfirm(true);
            return;
        }
        onClose();
    }, [hasUnsavedChanges, onClose]);

    const handleSaveFromUnsavedPrompt = useCallback(async () => {
        setShowUnsavedChangesConfirm(false);
        await saveRecipe({ closeAfterMetadataSave: true });
    }, [saveRecipe]);

    const handleDiscardUnsavedChanges = useCallback(() => {
        setShowUnsavedChangesConfirm(false);
        setEditingInstructionStep(null);
        onClose();
    }, [onClose]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await saveRecipe({ closeAfterMetadataSave: true });
    };

    // Debounced auto-save for metadata edits (no translation / nutrition)
    const saveMetadata = useCallback(async () => {
        if (!isEditMode || !recipeToEdit || isSaving || isBackgroundSaving) return;

        setIsBackgroundSaving(true);
        try {
            await persistMetadata();
            if (!majorChangesMade) {
                setBaselineSnapshot(buildCurrentSnapshot());
            }
        } catch (e) {
            console.error("Instant save failed", e);
        } finally {
            setIsBackgroundSaving(false);
        }
    }, [isEditMode, recipeToEdit, isSaving, isBackgroundSaving, persistMetadata, majorChangesMade, buildCurrentSnapshot]);

    useEffect(() => {
        if (!isEditMode) return;
        const timer = setTimeout(() => {
            saveMetadata();
        }, 1500); // 1.5s debounce
        return () => clearTimeout(timer);
    }, [name, servings, prepTime, category, suitableFor, dietTypes, lifestyleTags, isPublic, isSharedWithHouseholds, originLanguage, chefId]);

    const buildRecipeTextFromEditor = useCallback(() => formatRecipeText({
        name,
        servings,
        prepTime,
        ingredients: ingredients.map(stripIngredientForSave),
        instructions: instructionSteps,
        originNote,
    }, t), [name, servings, prepTime, ingredients, instructionSteps, originNote, t]);

    const openRecipeTextModal = useCallback(() => {
        const nextText = isEditMode ? buildRecipeTextFromEditor() : recipeText;
        setRecipeText(nextText);
        setShowRecipeTextModal(true);
    }, [isEditMode, recipeText, buildRecipeTextFromEditor]);

    const handleAiButtonClick = useCallback(() => {
        if (!hasAiAccess) {
            setShowGeminiKeyModal(true);
            return;
        }
        setShowAIBox((prev) => !prev);
    }, [hasAiAccess]);

    const handleSaveGeminiKey = useCallback(async (updates: { encryptedGeminiKey: string }) => {
        if (!onSaveUserProfile) {
            throw new Error('Profile save is unavailable.');
        }
        await onSaveUserProfile(updates);
        setShowGeminiKeyModal(false);
        setShowAIBox(true);
    }, [onSaveUserProfile]);

    const trimmedRecipeUrl = recipeUrl.trim();
    const trimmedRecipeText = recipeText.trim();
    const hasAiInput = !!(trimmedRecipeUrl || trimmedRecipeText);
    const hasBothAiInputs = !!(trimmedRecipeUrl && trimmedRecipeText);

    const handleParseRecipe = async () => {
        resetAiProgress();
        advanceAiStep(0);
        setIsLoading(true);
        startAutoAdvance(AI_PARSE_LAST_STEP_INDEX - 1);
        setError('');
        try {
            if (!hasAiInput) return;

            const useUrl = !!trimmedRecipeUrl;

            if (useUrl) {
                const conflict = await findConflictingDiscoverableRecipeForUrl(trimmedRecipeUrl, {
                    excludeRecipeId: recipeToEdit?.id,
                    knownRecipes: allRecipes,
                });
                if (conflict) {
                    setExistingRecipeConflict(conflict.recipe);
                    return;
                }
            }

            const parseOptions = useUrl
                ? { url: trimmedRecipeUrl, userId: currentUser?.uid }
                : { userId: currentUser?.uid };

            const result = await parseRecipeWithGemini(
                useUrl ? '' : trimmedRecipeText,
                originLanguage as Language,
                parseOptions,
                !currentUser?.isAiEligible ? currentUser?.encryptedGeminiKey : undefined
            );

            stopAutoAdvance();
            advanceAiStep(AI_PARSE_LAST_STEP_INDEX);

            setName(result.name);
            setServings(result.servings);
            setPrepTime(result.prepTime || 20);
            if (result.suitableFor?.length) {
                setSuitableFor(result.suitableFor);
            }
            if (result.dietTypes) {
                setDietTypes(normalizeDietTypes(result.dietTypes));
            }
            setLifestyleTags(estimateLifestyleTags({
                name: result.name,
                prepTime: result.prepTime || 20,
                ingredients: result.ingredients,
                category: result.category,
                instructions: result.instructions,
                suitableFor: result.suitableFor,
            }));
            if (result.category) {
                setCategory(result.category);
            }
            if (result.originLanguage) {
                setOriginLanguage(result.originLanguage);
            }
            
            setIngredients(withSectionKeys(result.ingredients, t('ingredients')));
            
            if (result.instructions) {
                let steps: InstructionStep[] = [];
                if (typeof result.instructions === 'string') {
                    steps = result.instructions.split('\n').filter(s => s.trim()).map(s => ({
                        id: `ai-step-${Math.random()}`,
                        type: 'step' as const,
                        content: s.trim()
                    }));
                } else if (Array.isArray(result.instructions)) {
                    steps = result.instructions as InstructionStep[];
                }
                
                if (steps.length === 0 || steps[0].type !== 'header') {
                    steps.unshift({ id: `ai-header-${Math.random()}`, type: 'header', content: t('prepTab') });
                }
                setInstructionSteps(steps);
            }

            // The AI result already includes the (normalized) source URL, added
            // server-side, so we don't re-add the raw pasted URL (which would only
            // differ by tracking params like ?igsh= and show up as a duplicate).
            const newLinks = result.links || [];

            setLinks(prev => mergeUniqueLinks(prev, newLinks));

            const urlsToMatch = useUrl
                ? [trimmedRecipeUrl, ...newLinks]
                : newLinks;

            let resolvedChefId: string | null = null;
            let resolvedChef: Chef | null = null;
            let createdChefFromInstagram = false;
            try {
                const credited = await creditChefFromParsedRecipe(result, availableChefs, {
                    userId: currentUser?.uid,
                    onResolveInstagramChef,
                    extraUrls: urlsToMatch,
                });
                if (credited.chef) {
                    resolvedChef = credited.chef;
                    resolvedChefId = credited.chef.id;
                    createdChefFromInstagram = credited.createdFromSource;
                    setSessionChefs((prev) => {
                        if (prev.some((chef) => chef.id === credited.chef!.id)) return prev;
                        return [...prev, credited.chef!];
                    });
                }
            } catch (chefError) {
                console.error('Chef auto-create failed:', chefError);
            }
            if (!resolvedChefId) {
                setAutoCreditedChef(null);
            }
            if (resolvedChefId) {
                setChefId(resolvedChefId);
                setAutoCreditedChef(createdChefFromInstagram && resolvedChef ? resolvedChef : null);
            }

            if (result.externalVideoId) {
                setExternalVideoId(result.externalVideoId);
            }
            if (result.externalVideoIdAliases?.length) {
                setExternalVideoIdAliases(result.externalVideoIdAliases);
            } else if (result.externalVideoId) {
                setExternalVideoIdAliases([result.externalVideoId]);
            }

            // AI re-analysis replaces ingredients/instructions — must use full save, not metadata-only.
            setMajorChangesMade(true);
            setShowAIBox(false);
        } catch (e) {
            console.error(e);
            if (e instanceof RecipeParseError && e.code === 'instagram_unavailable') {
                setShowScraperNotice(true);
            } else if (
                e instanceof RecipeParseError
                && (e.code === 'not_a_recipe' || e.code === 'inappropriate_content')
            ) {
                setContentRejection({
                    isOpen: true,
                    variant: e.code === 'inappropriate_content' ? 'misconduct' : 'not_recipe',
                });
            } else {
                setError(getRecipeParseErrorMessage(e, t));
            }
        } finally {
            stopAutoAdvance();
            setIsLoading(false);
        }
    };

    const groupedIngredients = useMemo(() => {
        const groups: Map<string, IngredientWithId[]> = new Map();
        const order: string[] = [];
        
        ingredients.forEach((ing) => {
            if (!groups.has(ing._sectionKey)) {
                groups.set(ing._sectionKey, []);
                order.push(ing._sectionKey);
            }
            groups.get(ing._sectionKey)!.push(ing);
        });
        
        return order.map((sectionKey) => [sectionKey, groups.get(sectionKey)!] as const);
    }, [ingredients]);

    const instructionSections = useMemo(() => {
        const sections: { header: InstructionStep; steps: { step: InstructionStep; originalIndex: number }[] }[] = [];
        let currentSection: typeof sections[0] | null = null;
        
        instructionSteps.forEach((s, idx) => {
            if (s.type === 'header') {
                currentSection = { header: s, steps: [] };
                sections.push(currentSection);
            } else if (currentSection) {
                currentSection.steps.push({ step: s, originalIndex: idx });
            }
        });
        return sections;
    }, [instructionSteps]);

    const updateIngredient = (id: string, field: keyof Ingredient, value: Ingredient[keyof Ingredient]) => {
        setMajorChangesMade(true);
        const idx = ingredients.findIndex(i => i._id === id);
        if (idx > -1) {
            const next = [...ingredients];
            next[idx] = { ...next[idx], [field]: value };
            setIngredients(next);
        }
    };

    const updateIngredientQuantity = (id: string, quantity: number, unit: string) => {
        setMajorChangesMade(true);
        const idx = ingredients.findIndex(i => i._id === id);
        if (idx > -1) {
            const next = [...ingredients];
            next[idx] = { ...next[idx], quantity, unit };
            setIngredients(next);
        }
    };

    const deleteIngredient = (id: string) => {
        setMajorChangesMade(true);
        setIngredients(ingredients.filter(i => i._id !== id));
        setSelectedIngredientId((prev) => (prev === id ? null : prev));
    };

    const toggleIngredientSelect = (id: string) => {
        setSelectedIngredientId((prev) => (prev === id ? null : id));
    };

    const selectIngredient = (id: string) => {
        setSelectedIngredientId(id);
    };

    const handleGalleryImageSelected = (file: File) => {
        setMajorChangesMade(true);
        setRemoveExistingImage(false);
        setImageUploadError(null);
        const previewUrl = URL.createObjectURL(file);

        if (recipeToEdit?.id) {
            void uploadRecipeImageNow(recipeToEdit, { file });
            return;
        }

        setPendingGalleryItems((items) => [...items, { kind: 'file', file, previewUrl }]);
    };

    const handleGalleryImageUrlSelected = (url: string) => {
        setMajorChangesMade(true);
        setRemoveExistingImage(false);
        setImageUploadError(null);

        if (recipeToEdit?.id) {
            void uploadRecipeImageNow(recipeToEdit, { sourceUrl: url });
            return;
        }

        setPendingGalleryItems((items) => [...items, { kind: 'url', sourceUrl: url, previewUrl: url }]);
    };

    const handleGalleryImageRemoveAtIndex = (index: number) => {
        setMajorChangesMade(true);
        setImageUploadError(null);

        if (index < persistedGalleryUrls.length) {
            if (recipeToEdit?.id) {
                void uploadRecipeImageNow(recipeToEdit, { removeIndex: index });
                return;
            }
            setRemoveExistingImage(true);
            setStoredImageFields(null);
            return;
        }

        const pendingIndex = index - persistedGalleryUrls.length;
        setPendingGalleryItems((items) => {
            const next = [...items];
            const [removed] = next.splice(pendingIndex, 1);
            if (removed?.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(removed.previewUrl);
            }
            return next;
        });
    };

    return (
        <div className="fixed inset-0 bg-bg-cream dark:bg-slate-950 z-[200] flex flex-col overflow-hidden font-rubik transition-colors duration-200">
            {isLoading && (
                <RecipeAiProgressOverlay
                    stepKey={aiStepKey}
                    stepIndex={aiStepIndex}
                    language={originLanguage || currentUser?.language}
                />
            )}
            
            <div className="h-16 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md z-[60] flex items-center justify-between px-6 border-b border-stone-200 dark:border-slate-800 flex-shrink-0">
                <button onClick={handleCancelRequest} className="text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors font-black text-sm uppercase tracking-widest px-4 py-2">{t('cancel')}</button>
                <div className="flex items-center gap-4">
                    <button 
                        onClick={handleAiButtonClick}
                        className="p-2.5 bg-slate-50 dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all active:scale-95 text-brand-orange"
                        title={hasAiAccess ? t('analyze') : t('enableAiFeatures')}
                    >
                        <SparklesIcon className="w-6 h-6" />
                    </button>
                    <button onClick={handleSubmit} disabled={isSaving || !name} className="hidden sm:block px-10 py-2.5 bg-brand-sage text-white font-black rounded-full shadow-lg shadow-brand-sage/20 hover:opacity-90 disabled:opacity-30">{isSaving ? '...' : t('save')}</button>
                </div>
            </div>

            <div className="flex-grow overflow-y-auto scrollbar-none">
                <div className="max-w-lg mx-auto px-4 py-10">
                    {showAIBox && (
                        <div className="mb-8 bg-brand-sage/5 dark:bg-brand-sage/10 rounded-3xl p-6 border border-brand-sage/10 animate-fade-in-down">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                        {t('link')}
                                    </label>
                                    <input
                                        type="text"
                                        value={recipeUrl}
                                        onChange={(e) => setRecipeUrl(e.target.value)}
                                        placeholder={t('pasteUrlPlaceholder')}
                                        className="w-full p-4 bg-white dark:bg-slate-800 border border-brand-sage/20 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-brand-sage outline-none"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                            {t('text')}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={openRecipeTextModal}
                                            className="text-[10px] font-black uppercase tracking-widest text-brand-sage hover:opacity-80 transition-opacity"
                                        >
                                            {t('editRecipeText')}
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={openRecipeTextModal}
                                        className="w-full p-4 bg-white dark:bg-slate-800 border border-brand-sage/20 rounded-2xl text-sm font-medium text-start hover:border-brand-sage/40 transition-colors"
                                    >
                                        {trimmedRecipeText
                                            ? t('recipeTextReady')
                                            : t('pasteText')}
                                    </button>
                                </div>

                                {hasBothAiInputs && (
                                    <p className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 rounded-xl px-4 py-3 leading-relaxed">
                                        {t('linkPreferredOverText')}
                                    </p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 mt-5">
                                <button type="button" onClick={() => setShowAIBox(false)} className="px-6 py-3 text-slate-400 font-bold text-xs uppercase tracking-widest hover:text-slate-600">{t('cancel')}</button>
                                <button type="button" onClick={handleParseRecipe} disabled={isLoading || !hasAiInput} className="px-8 py-3 bg-brand-orange text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-orange/20 hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                                    <SparklesIcon className="w-4 h-4" /> {t('analyze')}
                                </button>
                            </div>
                            {error && <p className="mt-3 text-red-500 text-xs font-bold text-center">{error}</p>}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-8">
                        <RecipeImagePicker
                            language={lang}
                            galleryUrls={galleryPreviewUrls}
                            onFileSelected={handleGalleryImageSelected}
                            onUrlSelected={handleGalleryImageUrlSelected}
                            onRemoveAtIndex={galleryPreviewUrls.length > 0 ? handleGalleryImageRemoveAtIndex : undefined}
                            isUploading={isSaving || isUploadingImage}
                            disabled={!canManage}
                        />
                        {imageUploadError && (
                            <p className="text-xs font-bold text-rose-600 text-center -mt-4">{imageUploadError}</p>
                        )}

                        <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-sm border border-stone-100 dark:border-slate-800 flex flex-col gap-6 transition-colors relative">
                            {isBackgroundSaving && (
                                <div className="absolute top-4 right-6 flex items-center gap-1.5 animate-pulse">
                                    <div className="w-1.5 h-1.5 bg-brand-sage rounded-full"></div>
                                    <span className="text-[8px] font-black text-brand-sage uppercase tracking-widest">{t('saving')}</span>
                                </div>
                            )}
                            <textarea 
                                ref={nameRef} rows={1} value={name} onChange={e => setName(e.target.value)}
                                placeholder={t('recipeName')}
                                className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xl sm:text-2xl font-nunito font-bold text-brand-sage focus:ring-2 focus:ring-brand-sage/30 focus:border-brand-sage/40 outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500 placeholder:font-medium tracking-tight resize-none overflow-hidden leading-snug"
                            />

                            {/* Compact Metrics Row */}
                            <div className="flex flex-nowrap items-stretch gap-1 bg-slate-50 dark:bg-slate-800/60 rounded-2xl p-1.5 border border-slate-100 dark:border-slate-700">
                                {/* Category */}
                                <div className="flex flex-col items-center gap-1 flex-1 min-w-0 px-1 py-1">
                                    <LayoutGridIcon className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" />
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value as RecipeCategory)}
                                        className="w-full max-w-[8.5rem] h-7 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-full text-xs font-bold text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-brand-sage/30 outline-none cursor-pointer appearance-none text-center px-2 truncate"
                                    >
                                        {RECIPE_CATEGORIES.map((c) => (
                                            <option key={c} value={c} className="text-slate-800 dark:text-slate-800">{t(c.toLowerCase())}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="w-px bg-slate-200 dark:bg-slate-700 self-stretch my-1 shrink-0" />

                                {/* Time */}
                                <div className="flex flex-col items-center gap-1 flex-1 min-w-0 px-1 py-1">
                                    <ClockIcon className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" />
                                    <SmartUnitCapsule 
                                        value={prepTime} 
                                        unit="mins" 
                                        onChange={(v, _u) => setPrepTime(v)} 
                                        language={lang}
                                        availableUnits={['mins']}
                                        step={1}
                                        bgClass="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 h-7 text-xs"
                                    />
                                </div>

                                <div className="w-px bg-slate-200 dark:bg-slate-700 self-stretch my-1 shrink-0" />

                                {/* Servings */}
                                <div className="flex flex-col items-center gap-1 flex-1 min-w-0 px-1 py-1">
                                    <UsersIcon className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" />
                                    <SmartUnitCapsule 
                                        value={servings} 
                                        unit="servings" 
                                        onChange={(v, _u) => setServings(v)} 
                                        language={lang}
                                        availableUnits={['servings']}
                                        step={1}
                                        bgClass="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 h-7 text-xs"
                                    />
                                </div>
                            </div>

                            {/* Best For */}
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">{t('bestFor')}</label>
                                <div className="flex flex-wrap gap-2">
                                    {(['Breakfast', 'Lunch', 'Dinner'] as MealType[]).map(meal => (
                                        <ToggleChip 
                                            key={meal} 
                                            label={t(meal === 'Breakfast' ? 'morning' : meal === 'Lunch' ? 'noon' : 'evening')} 
                                            checked={suitableFor.includes(meal)} 
                                            onClick={() => toggleSuitableFor(meal)} 
                                            icon={<span className="text-sm">{meal === 'Breakfast' ? '🌅' : meal === 'Lunch' ? '☀️' : '🌙'}</span>}
                                            className="flex-1 min-w-[5.5rem] basis-[calc(33.333%-0.5rem)]"
                                        />
                                    ))}
                                </div>
                            </div>

                            <div>
                                <p className="text-sm font-bold text-slate-400 dark:text-slate-500 mb-2">{t('characteristics')}</p>
                                <div className="flex flex-row flex-wrap gap-2 mb-6">
                                    {LIFESTYLE_TAG_CONFIG.map(({ key, labelKey, emoji }) => (
                                        <ToggleChip
                                            key={key}
                                            label={t(labelKey)}
                                            checked={lifestyleTags[key]}
                                            onClick={() => setLifestyleTags((prev) => ({ ...prev, [key]: !prev[key] }))}
                                            icon={<span className="text-sm leading-none">{emoji}</span>}
                                            className="rounded-full"
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Diet Labels */}
                            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">{t('dietLabels')}</label>
                                <div className="flex flex-wrap gap-2">
                                    {DIET_TYPE_CONFIG.map(({ key, labelKey, emoji }) => (
                                        <ToggleChip
                                            key={key}
                                            label={t(labelKey)}
                                            checked={dietTypes[key]}
                                            onClick={() => canEditDietTypes && setDietTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                                            icon={<span className="text-sm leading-none">{emoji}</span>}
                                            className={!canEditDietTypes ? 'opacity-60 pointer-events-none' : ''}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Visibility & Sharing */}
                            <div className="flex flex-col gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex flex-row flex-wrap gap-2">
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            if (isTemporaryDraft && managedHouseholdShareIds.length === 0) return;
                                            setIsSharedWithHouseholds(!isSharedWithHouseholds);
                                        }}
                                        disabled={isTemporaryDraft && managedHouseholdShareIds.length === 0}
                                        title={isTemporaryDraft ? t('temporaryRecipeVisibilityHint') : undefined}
                                        className={`flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border rounded-full shadow-sm transition-all active:bg-gray-50 dark:active:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed ${isSharedWithHouseholds ? 'border-brand-orange text-brand-orange bg-orange-50 dark:bg-orange-900/20' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'}`}
                                    >
                                        <HouseholdIcon className={`w-4 h-4 ${isSharedWithHouseholds ? 'text-brand-orange' : 'text-slate-400 dark:text-slate-500'}`} />
                                        <span className="text-xs font-bold">{t('shareWithHouseholds')}</span>
                                    </button>

                                    <ToggleChip 
                                        label={t('isPublic')} 
                                        checked={isPublic} 
                                        disabled={isTemporaryDraft}
                                        title={isTemporaryDraft ? t('temporaryRecipeVisibilityHint') : undefined}
                                        onClick={() => {
                                            if (isTemporaryDraft) return;
                                            setIsPublic(!isPublic);
                                        }} 
                                        icon={<GlobeIcon className={`w-4 h-4 ${isPublic ? 'text-brand-sage' : 'text-slate-400'}`} />} 
                                    />
                                </div>
                                {isTemporaryDraft && (
                                    <p className="text-[11px] font-bold text-amber-700 dark:text-amber-300 px-1">{t('temporaryRecipeVisibilityHint')}</p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex justify-between items-center px-4">
                                <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('ingredients')}</h3>
                            </div>

                            {groupedIngredients.map(([sectionKey, ings]) => {
                                const sectionName = ings[0]?.section ?? '';
                                const isCollapsed = collapsedIngSections.has(sectionKey);
                                const isDragged = draggedIngSection === sectionKey;
                                const isDropTarget = dropTargetIngSection === sectionKey;
                                const canDragThis = !isMobile || dragEnabledSection === sectionKey;

                                return (
                                    <div 
                                        key={sectionKey}
                                        draggable={canDragThis}
                                        onDragStart={(e) => onDragStartIngSection(e, sectionKey)}
                                        onDragOver={(e) => onDragOverIngSection(e, sectionKey)}
                                        onDragEnd={onDragEndIngSection}
                                        onDrop={(e) => onDropIngSection(e, sectionKey)}
                                        className={`
                                            bg-white dark:bg-slate-900 rounded-[2rem] border overflow-hidden shadow-sm transition-all duration-300
                                            ${isDragged ? 'opacity-40 scale-95 border-brand-orange' : 'border-stone-100 dark:border-slate-800'}
                                            ${isDropTarget ? 'border-brand-orange ring-2 ring-brand-orange/20 translate-y-2 shadow-lg' : ''}
                                            ${dragEnabledSection === sectionKey ? 'ring-4 ring-brand-orange/30 border-brand-orange' : ''}
                                        `}
                                    >
                                        <div className="flex justify-between items-center p-4 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer" onClick={() => toggleIngSection(sectionKey)}>
                                            <div className="flex items-center gap-3 flex-grow" onClick={e => e.stopPropagation()}>
                                                <div 
                                                    className={`p-1 transition-colors ${dragEnabledSection === sectionKey ? 'text-brand-orange' : 'text-slate-300 dark:text-slate-600 hover:text-slate-500'}`}
                                                    onContextMenu={(e) => isMobile && e.preventDefault()}
                                                    onTouchStart={() => handleHandleTouchStart(sectionKey)}
                                                    onTouchEnd={handleHandleTouchEnd}
                                                    onMouseDown={() => !isMobile && setDragEnabledSection(sectionKey)}
                                                >
                                                    <DragHandleIcon className={`w-5 h-5 ${isMobile ? 'scale-125' : ''}`} />
                                                </div>
                                                <div className={`text-slate-400 dark:text-slate-500 transition-transform duration-300 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => toggleIngSection(sectionKey)}>
                                                    <ChevronDownIcon className="w-4 h-4" />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    value={sectionName} 
                                                    onChange={(e) => handleRenameSection(sectionKey, e.target.value)} 
                                                    className="bg-transparent border-none focus:ring-0 p-0 text-sm font-black text-brand-orange uppercase tracking-tight w-full" 
                                                    placeholder={t('sectionNamePlaceholder')} 
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleAddIngredient(sectionKey, true); if (isCollapsed) toggleIngSection(sectionKey); }} className="p-1.5 bg-white dark:bg-slate-800 text-brand-sage rounded-lg shadow-sm border border-stone-100 dark:border-slate-700"><PlusIcon className="w-4 h-4" /></button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); if (window.confirm(t('delete') + '?')) setIngredients(ingredients.filter(i => i._sectionKey !== sectionKey)); }} className="p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400"><TrashIcon className="w-4 h-4" /></button>
                                            </div>
                                        </div>

                                        {!isCollapsed && (
                                            <div className="bg-white dark:bg-slate-900 px-4 pb-4" onDragStart={e => e.stopPropagation()}>
                                                {ings.map((ing) => (
                                                    <CompactIngredientRow 
                                                        key={ing._id} 
                                                        ing={ing} 
                                                        onChange={updateIngredient}
                                                        onQuantityChange={updateIngredientQuantity}
                                                        onDelete={deleteIngredient}
                                                        isSelected={selectedIngredientId === ing._id}
                                                        onToggleSelect={toggleIngredientSelect}
                                                        onSelect={selectIngredient}
                                                        language={lang}
                                                    />
                                                ))}
                                                <button type="button" onClick={() => handleAddIngredient(sectionKey)} className="w-full py-3 mt-1 text-slate-400 hover:text-brand-sage font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2 border-t border-slate-50 dark:border-slate-800"><PlusIcon className="w-3 h-3" /> {t('add')}</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <button type="button" onClick={handleAddIngredientSection} className="w-full py-4 border-2 border-dashed border-stone-200 dark:border-slate-700 text-brand-sage font-black text-xs uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-2xl flex items-center justify-center gap-2 transition-all">
                                <PlusIcon className="w-5 h-5" /> {t('addNewSection')}
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div className="flex justify-between items-center px-4">
                                <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('instructions')}</h3>
                            </div>
                            
                            {instructionSections.map((section, sectionIndex) => {
                                const isCollapsed = section.header.id ? collapsedPrepSections.has(section.header.id) : false;
                                const isDragged = section.header.id && draggedPrepSection === section.header.id;
                                const isDropTarget = section.header.id && dropTargetPrepSection === section.header.id;
                                const canDragThis = !isMobile || (section.header.id && dragEnabledSection === section.header.id);

                                return (
                                    <div 
                                        key={section.header.id || sectionIndex}
                                        draggable={canDragThis || false}
                                        onDragStart={(e) => section.header.id && onDragStartPrepSection(e, section.header.id)}
                                        onDragOver={(e) => section.header.id && onDragOverPrepSection(e, section.header.id)}
                                        onDragEnd={onDragEndPrepSection}
                                        onDrop={(e) => section.header.id && onDropPrepSection(e, section.header.id)}
                                        className={`
                                            bg-white dark:bg-slate-900 rounded-[2rem] border overflow-hidden shadow-sm transition-all duration-300
                                            ${isDragged ? 'opacity-40 scale-95 border-brand-sage' : 'border-stone-100 dark:border-slate-800'}
                                            ${isDropTarget ? 'border-brand-sage ring-2 ring-brand-sage/20 translate-y-2 shadow-lg' : ''}
                                            ${section.header.id && dragEnabledSection === section.header.id ? 'ring-4 ring-brand-sage/30 border-brand-sage' : ''}
                                        `}
                                    >
                                        <div className="flex justify-between items-center p-4 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer group" onClick={() => section.header.id && togglePrepSection(section.header.id)}>
                                            <div className="flex items-center gap-3 flex-grow" onClick={e => e.stopPropagation()}>
                                                <div 
                                                    className={`p-1 transition-colors ${section.header.id && dragEnabledSection === section.header.id ? 'text-brand-sage' : 'text-slate-300 dark:text-slate-600 hover:text-slate-500'}`}
                                                    onContextMenu={(e) => isMobile && e.preventDefault()}
                                                    onTouchStart={() => section.header.id && handleHandleTouchStart(section.header.id)}
                                                    onTouchEnd={handleHandleTouchEnd}
                                                    onMouseDown={() => !isMobile && section.header.id && setDragEnabledSection(section.header.id)}
                                                >
                                                    <DragHandleIcon className={`w-5 h-5 ${isMobile ? 'scale-125' : ''}`} />
                                                </div>
                                                <div className={`text-slate-400 dark:text-slate-500 transition-transform duration-300 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => section.header.id && togglePrepSection(section.header.id)}>
                                                    <ChevronDownIcon className="w-4 h-4" />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    value={section.header.content} 
                                                    onChange={(e) => section.header.id && handleInstructionChange(section.header.id, e.target.value)} 
                                                    className="bg-transparent border-none focus:ring-0 p-0 text-sm font-black text-brand-sage uppercase tracking-tight w-full" 
                                                    placeholder={t('prepSectionPlaceholder')} 
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {sectionIndex > 0 && section.header.id && (
                                                    <button 
                                                        type="button" 
                                                        onClick={(e) => { e.stopPropagation(); if (section.header.id) handleUniteWithPrevious(section.header.id); }}
                                                        className="p-2 text-slate-400 dark:text-sky-400/80 hover:text-brand-sage dark:hover:text-sky-400 transition-all active:scale-90"
                                                        title={t('unite')}
                                                    >
                                                        <MergeIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                                <button type="button" onClick={(e) => { e.stopPropagation(); if (section.header.id) handleInstructionDelete(section.header.id); }} className="p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400"><TrashIcon className="w-4 h-4" /></button>
                                            </div>
                                        </div>

                                        {!isCollapsed && (
                                            <div className="bg-white dark:bg-slate-900 p-4 space-y-4" onDragStart={e => e.stopPropagation()}>
                                                {section.steps.length === 0 && <p className="text-center text-xs text-slate-400 dark:text-slate-500 py-2">{t('noStepsYet')}</p>}
                                                {section.steps.map(({ step, originalIndex }, stepIndex) => {
                                                    const canMoveUp = originalIndex > 0 && instructionSteps[originalIndex - 1]?.type === 'step';
                                                    const canMoveDown = originalIndex < instructionSteps.length - 1 && instructionSteps[originalIndex + 1]?.type === 'step';

                                                    return (
                                                    <div key={step.id || originalIndex} className="space-y-1">
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => openInstructionStepEditor(step, stepIndex + 1)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                openInstructionStepEditor(step, stepIndex + 1);
                                                            }
                                                        }}
                                                        className="flex gap-4 sm:gap-6 items-start cursor-pointer rounded-2xl -mx-2 px-2 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openInstructionStepEditor(step, stepIndex + 1);
                                                            }}
                                                            className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center font-black text-lg border shadow-sm transition-all active:scale-95 bg-brand-sage/5 dark:bg-brand-sage/10 text-brand-sage border-brand-sage/10 group-hover:bg-brand-sage/10 dark:group-hover:bg-brand-sage/20"
                                                        >
                                                            {stepIndex + 1}
                                                        </button>
                                                        <div className="flex-grow min-w-0 pt-1">
                                                            <p className={`text-base sm:text-lg leading-relaxed font-medium whitespace-pre-wrap ${step.content.trim() ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500 italic'}`}>
                                                                {step.content.trim() || t('describeStepPlaceholder')}
                                                            </p>
                                                            {typeof step.durationMinutes === 'number' && step.durationMinutes > 0 && (
                                                                <span className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[11px] font-bold bg-brand-sage/10 text-brand-sage border border-brand-sage/15">
                                                                    {step.durationMinutes}m
                                                                </span>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (step.id) handleInstructionDelete(step.id);
                                                            }}
                                                            className="mt-2 p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-400 dark:hover:text-red-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0"
                                                            aria-label={t('delete')}
                                                        >
                                                            <XIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <div className="flex gap-4 sm:gap-6 -mx-2 px-2">
                                                        <div className="w-10 shrink-0" aria-hidden="true" />
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                disabled={!canMoveUp}
                                                                onClick={() => handleMoveInstruction(originalIndex, -1)}
                                                                className="p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:text-brand-sage dark:hover:text-brand-sage hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-25 disabled:pointer-events-none transition-colors"
                                                                aria-label={t('moveUp')}
                                                            >
                                                                <ChevronUpIcon className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={!canMoveDown}
                                                                onClick={() => handleMoveInstruction(originalIndex, 1)}
                                                                className="p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:text-brand-sage dark:hover:text-brand-sage hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-25 disabled:pointer-events-none transition-colors"
                                                                aria-label={t('moveDown')}
                                                            >
                                                                <ChevronDownIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    </div>
                                                    );
                                                })}
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleInstructionAddStep(instructionSteps.indexOf(section.header))} 
                                                    className="w-full py-3 mt-2 border border-slate-100 dark:border-slate-800 rounded-xl text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-bold flex items-center justify-center gap-2 transition-all"
                                                >
                                                    <PlusIcon className="w-4 h-4" /> {t('addStepTo').replace('{section}', section.header.content)}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <button type="button" onClick={handleInstructionAddHeader} className="w-full py-4 border-2 border-dashed border-stone-200 dark:border-slate-700 text-brand-sage font-black text-xs uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-2xl flex items-center justify-center gap-2 transition-all">
                                <PlusIcon className="w-5 h-5" /> {t('addNewSection')}
                            </button>
                        </div>

                        <div className="px-1">
                            <NutritionFactsPanel
                                facts={nutritionFacts}
                                language={lang}
                                collapsed={isNutritionCollapsed}
                                onToggleCollapse={() => setIsNutritionCollapsed((prev) => !prev)}
                                isLoading={isSaving && !nutritionFacts}
                                showRecalculate={isEditMode && canManage && canUseAi}
                                isRecalculating={isRecalculatingNutrition}
                                onRecalculate={handleRecalculateNutrition}
                            />
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight px-2">{t('links')}</h3>
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-stone-100 dark:border-slate-800 space-y-4">
                                {links.map((link, idx) => {
                                    const safeHref = sanitizeExternalHref(link);
                                    return (
                                    <div key={idx} className="flex gap-2 items-center bg-slate-50 dark:bg-slate-800 p-3 rounded-xl">
                                        <LinkIcon className="w-4 h-4 text-sky-500 flex-shrink-0" />
                                        {safeHref ? (
                                            <a href={safeHref} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-600 dark:text-sky-400 hover:underline truncate flex-grow block">
                                                {link}
                                            </a>
                                        ) : (
                                            <span className="text-sm text-slate-500 dark:text-slate-400 truncate flex-grow block">{link}</span>
                                        )}
                                        <CopyButton
                                            text={link}
                                            copyLabel={t('copyLink')}
                                            copiedLabel={t('copied')}
                                        />
                                        <button type="button" onClick={() => handleDeleteLink(idx)} className="text-slate-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>
                                    </div>
                                    );
                                })}
                                <div className="flex gap-2">
                                    <input 
                                        type="url" 
                                        value={tempLink} 
                                        onChange={(e) => setTempLink(e.target.value)} 
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddLink(e)}
                                        placeholder={t('pasteUrlPlaceholder')}
                                        className="flex-grow bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-sky-500 outline-none"
                                    />
                                    <button type="button" onClick={handleAddLink} className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all font-bold text-xs uppercase tracking-wider">
                                        {t('add')}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight px-2">{t('creditsAndSources')}</h3>
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-stone-100 dark:border-slate-800 space-y-4">
                                <ChefAutocomplete
                                    chefs={availableChefs}
                                    value={chefId}
                                    onChange={setChefId}
                                    currentUserId={currentUser?.uid}
                                    language={currentUser?.language}
                                    label={t('chefCredit')}
                                />
                                {autoCreditedChef && (
                                    <p className="text-xs font-bold text-brand-sage px-1">
                                        {t('chefAutoCredited').replace('{name}', autoCreditedChef.englishName)}
                                    </p>
                                )}
                                <div>
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 px-1">{t('creditOriginNote')}</label>
                                <textarea 
                                    value={originNote} 
                                    onChange={(e) => setOriginNote(e.target.value)} 
                                    placeholder={t('originNotePlaceholder')}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-sm focus:ring-2 focus:ring-sky-500 outline-none resize-none h-24"
                                />
                                </div>
                            </div>
                        </div>

                        <div className="h-24" />
                    </form>
                </div>
            </div>

            <div className="sticky bottom-0 w-full bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 p-4 pb-8 flex justify-between items-center z-50 flex-shrink-0 shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.05)] transition-colors">
                <button onClick={handleCancelRequest} className="px-6 py-3 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 font-black rounded-full hover:bg-slate-50 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs">{t('cancel')}</button>
                <div className="flex gap-3">
                    {isEditMode && <button type="button" onClick={() => setShowDeleteConfirm(true)} className="sm:hidden p-3 text-red-500"><TrashIcon className="w-6 h-6" /></button>}
                    <button onClick={handleSubmit} disabled={isSaving || !name} className="px-10 py-3 bg-brand-sage text-white font-black rounded-full shadow-2xl shadow-brand-sage/30 hover:opacity-90 disabled:opacity-30 transition-all uppercase tracking-widest text-xs">{isSaving ? '...' : t('save')}</button>
                </div>
            </div>

            <ConfirmationModal isOpen={showDeleteConfirm} title={t('deleteRecipe')} message={`${t('deleteRecipe')}? "${name}"`} confirmText={t('delete')} cancelText={t('cancel')} onConfirm={() => { if (isEditMode && recipeToEdit && onDeleteRecipe) { onDeleteRecipe(recipeToEdit.id); onClose(); } }} onCancel={() => setShowDeleteConfirm(false)} />

            {showUnsavedChangesConfirm && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[290] p-4 animate-fade-in"
                    onClick={() => setShowUnsavedChangesConfirm(false)}
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-[360px] overflow-hidden border border-slate-100 dark:border-slate-800"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-8 pb-6 text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-500 mb-6">
                                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 15c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 mb-3">{t('unsavedRecipeChangesTitle')}</h3>
                            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{t('unsavedRecipeChangesMessage')}</p>
                        </div>
                        <div className="p-4 pt-0 space-y-2">
                            <button
                                type="button"
                                onClick={handleSaveFromUnsavedPrompt}
                                disabled={isSaving || isBackgroundSaving}
                                className="w-full px-4 py-3 bg-brand-sage text-white font-bold rounded-xl shadow-sm hover:opacity-90 transition-colors text-sm disabled:opacity-40"
                            >
                                {t('saveNow')}
                            </button>
                            <button
                                type="button"
                                onClick={handleDiscardUnsavedChanges}
                                className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm"
                            >
                                {t('discardChanges')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowUnsavedChangesConfirm(false)}
                                className="w-full px-4 py-3 text-slate-500 dark:text-slate-400 font-bold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors text-sm"
                            >
                                {t('keepEditing')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {editingInstructionStep && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[280] p-0 sm:p-4 animate-fade-in"
                    onClick={discardInstructionStepEdit}
                >
                    <div
                        className="bg-white dark:bg-slate-900 w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl border border-stone-100 dark:border-slate-800 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="text-lg font-black text-slate-800 dark:text-slate-100">
                                {t('instructions')} · {editingInstructionStep.stepNumber}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <textarea
                                ref={instructionStepEditorRef}
                                value={editingInstructionStep.draft}
                                onChange={(e) => setEditingInstructionStep((prev) => prev ? { ...prev, draft: e.target.value } : prev)}
                                rows={6}
                                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 text-base leading-relaxed text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-sage/30 outline-none resize-y min-h-[9rem]"
                                placeholder={t('describeStepPlaceholder')}
                            />
                            <label className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-300">
                                <span className="font-bold">{t('instructionStepDuration')}</span>
                                <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    inputMode="numeric"
                                    value={editingInstructionStep.draftDurationMinutes}
                                    onChange={(e) => setEditingInstructionStep((prev) => prev ? { ...prev, draftDurationMinutes: e.target.value } : prev)}
                                    placeholder={t('instructionStepDurationOptional')}
                                    className="w-full sm:w-40 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-base text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-sage/30 outline-none"
                                />
                            </label>
                        </div>
                        <div className="px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2 flex gap-3">
                            <button
                                type="button"
                                onClick={discardInstructionStepEdit}
                                className="flex-1 py-3 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 font-black rounded-full hover:bg-slate-50 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={acceptInstructionStepEdit}
                                className="flex-1 py-3 bg-brand-sage text-white font-black rounded-full shadow-lg shadow-brand-sage/20 hover:opacity-90 transition-all uppercase tracking-widest text-xs"
                            >
                                {t('save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <InfoNoticeModal
                isOpen={showScraperNotice}
                onClose={() => setShowScraperNotice(false)}
                title={t('scraperUnavailableTitle')}
                message={t('scraperUnavailableMsg')}
                variant="info"
                language={lang}
            />
            <InfoNoticeModal
                isOpen={!!existingRecipeConflict && !sharingConflictNotice}
                onClose={() => setExistingRecipeConflict(null)}
                title={t('recipeAlreadyExistsTitle')}
                message={existingRecipeConflict ? t('recipeAlreadyExistsMsg').replace('{name}', existingRecipeConflict.name) : undefined}
                variant="info"
                language={lang}
                actionLabel={onViewExistingRecipe ? t('viewRecipe') : undefined}
                onAction={existingRecipeConflict && onViewExistingRecipe
                    ? () => onViewExistingRecipe(existingRecipeConflict)
                    : undefined}
            />
            <InfoNoticeModal
                isOpen={sharingConflictNotice}
                onClose={() => setSharingConflictNotice(false)}
                title={t('recipeAlreadyExistsTitle')}
                message={t('recipeSharingConflictMsg')}
                variant="info"
                language={lang}
                actionLabel={existingRecipeConflict && onViewExistingRecipe ? t('viewRecipe') : undefined}
                onAction={existingRecipeConflict && onViewExistingRecipe
                    ? () => onViewExistingRecipe(existingRecipeConflict)
                    : undefined}
            />
            <RecipeContentRejectionModal
                isOpen={!!contentRejection?.isOpen}
                onClose={() => setContentRejection(null)}
                variant={contentRejection?.variant ?? 'not_recipe'}
                language={lang}
            />
            <GeminiApiKeyModal
                isOpen={showGeminiKeyModal}
                onClose={() => setShowGeminiKeyModal(false)}
                onSave={handleSaveGeminiKey}
                language={lang}
            />
            <RecipeTextInputModal
                isOpen={showRecipeTextModal}
                onClose={() => setShowRecipeTextModal(false)}
                onSave={setRecipeText}
                initialText={recipeText}
                language={lang}
            />
        </div>
    );
};

export default AddRecipeModal;
